/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * API specification
 * OpenAPI spec version: 0.1.0
 */
import * as zod from "zod";

/**
 * @summary Recover this account's completed quote after a disconnected response
 */
export const GetQuoteGenerationResultParams = zod.object({
  requestId: zod.coerce.string().uuid(),
});

export const GetQuoteGenerationResultResponse = zod.object({
  status: zod.enum([
    "pending",
    "running",
    "completed",
    "failed",
    "unconfirmed",
  ]),
  message: zod.string().optional(),
  result: zod.record(zod.string(), zod.unknown()).optional(),
});

/**
 * @summary Resolve quote-local purchase materials automatically without changing quoted prices
 */
export const proposeQuoteMaterialsBodyLineQuantityExclusiveMin = 0;

export const proposeQuoteMaterialsBodyLineUnitCostMin = 0;

export const proposeQuoteMaterialsBodyLineMaterialCostMin = 0;

export const ProposeQuoteMaterialsBody = zod.object({
  libraryOnly: zod
    .boolean()
    .optional()
    .describe(
      "Recheck existing pending materials against the library without generating a new AI estimate.",
    ),
  line: zod.object({
    name: zod.string(),
    description: zod.string().nullish(),
    category: zod.string(),
    quantity: zod
      .number()
      .gt(proposeQuoteMaterialsBodyLineQuantityExclusiveMin),
    unit: zod.string(),
    unitCost: zod.number().min(proposeQuoteMaterialsBodyLineUnitCostMin),
    materialCost: zod
      .number()
      .min(proposeQuoteMaterialsBodyLineMaterialCostMin)
      .optional(),
    costLibraryTaskId: zod.string().nullish(),
    quantityDerivation: zod.record(zod.string(), zod.unknown()).nullish(),
  }),
});

export const proposeQuoteMaterialsResponseMaterialsItemQuantityExclusiveMin = 0;

export const proposeQuoteMaterialsResponseMaterialsItemUnitPriceMin = 0;

export const proposeQuoteMaterialsResponseMaterialsMax = 30;

export const ProposeQuoteMaterialsResponse = zod.object({
  source: zod
    .enum(["library", "estimated", "unavailable"])
    .optional()
    .describe(
      "Server-assigned provenance. Missing provenance is treated as an unverified estimate.",
    ),
  noMaterialsReason: zod.string().optional(),
  materials: zod
    .array(
      zod.object({
        name: zod.string(),
        specification: zod.string(),
        quantity: zod
          .number()
          .gt(proposeQuoteMaterialsResponseMaterialsItemQuantityExclusiveMin),
        unit: zod.string(),
        unitPrice: zod
          .number()
          .min(proposeQuoteMaterialsResponseMaterialsItemUnitPriceMin),
        assumption: zod.string(),
      }),
    )
    .max(proposeQuoteMaterialsResponseMaterialsMax),
});

export const ListPriceResearchResponse = zod.record(
  zod.string(),
  zod.unknown(),
);

export const ActOnPriceResearchBody = zod.object({
  action: zod.enum([
    "preview-import",
    "import",
    "detail",
    "decide",
    "preview-apply",
    "apply",
    "preview-undo",
    "undo",
  ]),
  researchType: zod.enum(["materials", "labour"]).optional(),
  batchId: zod.string().optional(),
  revision: zod.number().optional(),
  token: zod.string().optional(),
  name: zod.string().optional(),
  files: zod
    .array(
      zod.object({
        name: zod.string(),
        kind: zod.enum(["prices", "factors", "notes"]),
        content: zod.string(),
      }),
    )
    .optional(),
  mapping: zod.record(zod.string(), zod.string()).optional(),
  changes: zod
    .array(
      zod.object({
        id: zod.string(),
        decision: zod
          .enum(["pending", "approved", "rejected", "deferred"])
          .optional(),
        proposedPrice: zod.number().optional(),
        proposedUnit: zod.string().optional(),
        proposedFactor: zod.number().optional(),
        proposedWaste: zod.number().optional(),
        targetKey: zod.string().optional(),
        note: zod.string().optional(),
        contextConfirmed: zod.boolean().optional(),
      }),
    )
    .optional(),
  selectedIds: zod.array(zod.string()).optional(),
  confirm: zod.boolean().optional(),
});

export const ActOnPriceResearchResponse = zod.record(
  zod.string(),
  zod.unknown(),
);

export const getCostTaskFavouritesResponseTaskIdMax = 200;

export const GetCostTaskFavouritesResponseItem = zod.object({
  source: zod.enum(["default", "custom"]),
  taskId: zod.string().min(1).max(getCostTaskFavouritesResponseTaskIdMax),
});
export const GetCostTaskFavouritesResponse = zod.array(
  GetCostTaskFavouritesResponseItem,
);

export const setCostTaskFavouriteBodyTaskIdMax = 200;

export const SetCostTaskFavouriteBody = zod.object({
  source: zod.enum(["default", "custom"]),
  taskId: zod.string().min(1).max(setCostTaskFavouriteBodyTaskIdMax),
  favourite: zod.boolean(),
});

export const ListRecipeDraftsResponse = zod.object({
  rows: zod.array(
    zod.object({
      id: zod.string(),
      name: zod.string(),
      description: zod.string().nullish(),
      notesIncluded: zod.string().nullish(),
      notesExcluded: zod.string().nullish(),
      reviewWarnings: zod.array(zod.string()).optional(),
      category: zod.string(),
      unit: zod.string(),
      currentTypical: zod.string(),
      hasLiveRecipe: zod.boolean(),
      draftId: zod.string().nullable(),
      generationState: zod
        .union([
          zod.literal("queued"),
          zod.literal("running"),
          zod.literal("done"),
          zod.literal("failed"),
          zod.literal(null),
        ])
        .nullable(),
      status: zod.enum([
        "not_drafted",
        "draft",
        "approved",
        "rejected",
        "redraft_requested",
      ]),
      note: zod.string(),
      ownerNote: zod.string(),
      revision: zod.number(),
      isStale: zod.boolean(),
      invalid: zod.boolean(),
      derivedTypical: zod.string().nullable(),
      sumDifference: zod.string().nullable(),
      usageRank: zod.number().nullable(),
    }),
  ),
  progress: zod.object({
    total: zod.number(),
    eligible: zod.number(),
    approved: zod.number(),
    top50Approved: zod.number(),
    drafted: zod.number(),
    failed: zod.number(),
    queued: zod.number(),
    running: zod.number(),
    firstBalanced: zod.number(),
    labourFloorAdjusted: zod.number(),
    retries: zod.number(),
  }),
  safety: zod.object({
    capturedAt: zod.string().nullable(),
    ratesMatch: zod.boolean(),
    quotesMatch: zod.boolean(),
    message: zod.string().nullable(),
    active: zod.boolean(),
  }),
  runActive: zod.boolean(),
});

export const startRecipeDraftsBodyRetryFailuresDefault = false;

export const StartRecipeDraftsBody = zod.object({
  retryFailures: zod
    .boolean()
    .default(startRecipeDraftsBodyRetryFailuresDefault),
});

export const StartRecipeDraftsResponse = zod.object({
  queued: zod.boolean(),
});

export const RefreshRecipeDraftBaselineBody = zod.object({
  confirm: zod.literal(true),
});

export const RefreshRecipeDraftBaselineResponse = zod.object({
  capturedAt: zod.string().nullable(),
  ratesMatch: zod.boolean(),
  quotesMatch: zod.boolean(),
  message: zod.string().nullable(),
  active: zod.boolean(),
});

export const approveRecipeDraftsBodyTaskIdsMax = 100;

export const approveRecipeDraftsBodyEditedRecipeLabourAllowanceMin = 0;

export const approveRecipeDraftsBodyEditedRecipePlantAllowanceMin = 0;

export const approveRecipeDraftsBodyEditedRecipeSupplyAllowanceMin = 0;

export const approveRecipeDraftsBodyEditedRecipeSupplyPricingReferenceQuantityExclusiveMin = 0;

export const approveRecipeDraftsBodyEditedRecipeSupplyPricingDimensionLabelsMax = 3;

export const approveRecipeDraftsBodyEditedRecipeSupplyPricingFixedCostMin = 0;

export const approveRecipeDraftsBodyEditedRecipeSupplyPricingReferencePriceMin = 0;

export const approveRecipeDraftsBodyEditedRecipeSupplyPricingActualQuantityMin = 0;

export const approveRecipeDraftsBodyEditedRecipeSupplyPricingResolvedQuantityMin = 0;

export const approveRecipeDraftsBodyEditedRecipeQuotePricingSelectedRateMin = 0;

export const approveRecipeDraftsBodyEditedRecipeQuotePricingBaselineMaterialsMin = 0;

export const approveRecipeDraftsBodyEditedRecipeQuotePricingBaselineLabourMin = 0;

export const approveRecipeDraftsBodyEditedRecipeQuotePricingBaselinePlantMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemBaseFactorMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemWastePctMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemQuantityPerUnitMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemUnitPriceMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemPackSizeExclusiveMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingFixedCostMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingReferencePriceMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingActualQuantityMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const approveRecipeDraftsBodyEditedRecipeMaterialsMax = 100;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemBaseFactorMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemWastePctMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemQuantityPerUnitMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemUnitPriceMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemPackSizeExclusiveMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingFixedCostMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingReferencePriceMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingActualQuantityMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const approveRecipeDraftsBodyEditedRecipeOriginalMaterialsMax = 100;

export const approveRecipeDraftsBodyExpectedRevisionMin = 0;

export const ApproveRecipeDraftsBody = zod.object({
  taskIds: zod
    .array(zod.string())
    .min(1)
    .max(approveRecipeDraftsBodyTaskIdsMax),
  editedRecipe: zod
    .object({
      version: zod.literal(1),
      shape: zod.enum(["formula", "specification"]),
      labour_allowance: zod
        .number()
        .min(approveRecipeDraftsBodyEditedRecipeLabourAllowanceMin),
      plant_allowance: zod
        .number()
        .min(approveRecipeDraftsBodyEditedRecipePlantAllowanceMin),
      specification: zod.string().optional(),
      supply_allowance: zod
        .number()
        .min(approveRecipeDraftsBodyEditedRecipeSupplyAllowanceMin)
        .optional(),
      supply_name: zod.string().optional(),
      supply_material_id: zod.string().optional(),
      supply_pricing: zod
        .object({
          measure: zod.enum(["length", "area", "volume", "weight", "count"]),
          reference_quantity: zod
            .number()
            .gt(
              approveRecipeDraftsBodyEditedRecipeSupplyPricingReferenceQuantityExclusiveMin,
            ),
          dimension_labels: zod
            .array(zod.string())
            .min(1)
            .max(
              approveRecipeDraftsBodyEditedRecipeSupplyPricingDimensionLabelsMax,
            ),
          fixed_cost: zod
            .number()
            .min(approveRecipeDraftsBodyEditedRecipeSupplyPricingFixedCostMin),
          reference_price: zod
            .number()
            .min(
              approveRecipeDraftsBodyEditedRecipeSupplyPricingReferencePriceMin,
            )
            .optional(),
          actual_quantity: zod
            .number()
            .min(
              approveRecipeDraftsBodyEditedRecipeSupplyPricingActualQuantityMin,
            )
            .optional(),
          resolved_quantity: zod
            .number()
            .min(
              approveRecipeDraftsBodyEditedRecipeSupplyPricingResolvedQuantityMin,
            )
            .optional(),
          resolved_spec: zod.string().optional(),
          section_rates: zod
            .array(
              zod.object({
                section: zod.string(),
                grade: zod.string(),
                finish: zod.string(),
                rate_per_metre: zod.number(),
                supplier: zod.string(),
                source_url: zod.string(),
                checked_on: zod.string(),
              }),
            )
            .optional(),
          length_label: zod.string().optional(),
          selected_section: zod.string().optional(),
          resolved_section: zod.string().optional(),
          evidence: zod.string(),
        })
        .optional(),
      quote_pricing: zod
        .object({
          selected_rate: zod
            .number()
            .min(
              approveRecipeDraftsBodyEditedRecipeQuotePricingSelectedRateMin,
            ),
          baseline_materials: zod
            .number()
            .min(
              approveRecipeDraftsBodyEditedRecipeQuotePricingBaselineMaterialsMin,
            ),
          baseline_labour: zod
            .number()
            .min(
              approveRecipeDraftsBodyEditedRecipeQuotePricingBaselineLabourMin,
            ),
          baseline_plant: zod
            .number()
            .min(
              approveRecipeDraftsBodyEditedRecipeQuotePricingBaselinePlantMin,
            ),
          status: zod.enum(["calculated", "needs_measurements", "manual"]),
          notes: zod.array(zod.string()),
        })
        .optional(),
      status: zod.enum(["none", "reviewed", "user_edited"]),
      materials: zod
        .array(
          zod.object({
            material_id: zod.string(),
            name: zod.string(),
            spec: zod.string().optional(),
            base_factor: zod
              .number()
              .min(
                approveRecipeDraftsBodyEditedRecipeMaterialsItemBaseFactorMin,
              ),
            waste_pct: zod
              .number()
              .min(approveRecipeDraftsBodyEditedRecipeMaterialsItemWastePctMin),
            quantity_per_unit: zod
              .number()
              .min(
                approveRecipeDraftsBodyEditedRecipeMaterialsItemQuantityPerUnitMin,
              )
              .optional(),
            unit: zod.string(),
            unit_price: zod
              .number()
              .min(
                approveRecipeDraftsBodyEditedRecipeMaterialsItemUnitPriceMin,
              ),
            pack_size: zod
              .number()
              .gt(
                approveRecipeDraftsBodyEditedRecipeMaterialsItemPackSizeExclusiveMin,
              )
              .optional(),
            round_packs: zod.boolean().optional(),
            assumption: zod.string(),
            dimension_pricing: zod
              .object({
                measure: zod.enum([
                  "length",
                  "area",
                  "volume",
                  "weight",
                  "count",
                ]),
                reference_quantity: zod
                  .number()
                  .gt(
                    approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                  ),
                dimension_labels: zod
                  .array(zod.string())
                  .min(1)
                  .max(
                    approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingDimensionLabelsMax,
                  ),
                fixed_cost: zod
                  .number()
                  .min(
                    approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingFixedCostMin,
                  ),
                reference_price: zod
                  .number()
                  .min(
                    approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingReferencePriceMin,
                  )
                  .optional(),
                actual_quantity: zod
                  .number()
                  .min(
                    approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingActualQuantityMin,
                  )
                  .optional(),
                resolved_quantity: zod
                  .number()
                  .min(
                    approveRecipeDraftsBodyEditedRecipeMaterialsItemDimensionPricingResolvedQuantityMin,
                  )
                  .optional(),
                resolved_spec: zod.string().optional(),
                section_rates: zod
                  .array(
                    zod.object({
                      section: zod.string(),
                      grade: zod.string(),
                      finish: zod.string(),
                      rate_per_metre: zod.number(),
                      supplier: zod.string(),
                      source_url: zod.string(),
                      checked_on: zod.string(),
                    }),
                  )
                  .optional(),
                length_label: zod.string().optional(),
                selected_section: zod.string().optional(),
                resolved_section: zod.string().optional(),
                evidence: zod.string(),
              })
              .optional(),
          }),
        )
        .max(approveRecipeDraftsBodyEditedRecipeMaterialsMax),
      original_materials: zod
        .array(
          zod.object({
            material_id: zod.string(),
            name: zod.string(),
            spec: zod.string().optional(),
            base_factor: zod
              .number()
              .min(
                approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemBaseFactorMin,
              ),
            waste_pct: zod
              .number()
              .min(
                approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemWastePctMin,
              ),
            quantity_per_unit: zod
              .number()
              .min(
                approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemQuantityPerUnitMin,
              )
              .optional(),
            unit: zod.string(),
            unit_price: zod
              .number()
              .min(
                approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemUnitPriceMin,
              ),
            pack_size: zod
              .number()
              .gt(
                approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemPackSizeExclusiveMin,
              )
              .optional(),
            round_packs: zod.boolean().optional(),
            assumption: zod.string(),
            dimension_pricing: zod
              .object({
                measure: zod.enum([
                  "length",
                  "area",
                  "volume",
                  "weight",
                  "count",
                ]),
                reference_quantity: zod
                  .number()
                  .gt(
                    approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                  ),
                dimension_labels: zod
                  .array(zod.string())
                  .min(1)
                  .max(
                    approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingDimensionLabelsMax,
                  ),
                fixed_cost: zod
                  .number()
                  .min(
                    approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingFixedCostMin,
                  ),
                reference_price: zod
                  .number()
                  .min(
                    approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingReferencePriceMin,
                  )
                  .optional(),
                actual_quantity: zod
                  .number()
                  .min(
                    approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingActualQuantityMin,
                  )
                  .optional(),
                resolved_quantity: zod
                  .number()
                  .min(
                    approveRecipeDraftsBodyEditedRecipeOriginalMaterialsItemDimensionPricingResolvedQuantityMin,
                  )
                  .optional(),
                resolved_spec: zod.string().optional(),
                section_rates: zod
                  .array(
                    zod.object({
                      section: zod.string(),
                      grade: zod.string(),
                      finish: zod.string(),
                      rate_per_metre: zod.number(),
                      supplier: zod.string(),
                      source_url: zod.string(),
                      checked_on: zod.string(),
                    }),
                  )
                  .optional(),
                length_label: zod.string().optional(),
                selected_section: zod.string().optional(),
                resolved_section: zod.string().optional(),
                evidence: zod.string(),
              })
              .optional(),
          }),
        )
        .max(approveRecipeDraftsBodyEditedRecipeOriginalMaterialsMax)
        .optional()
        .describe(
          "Server-owned baseline captured before the first protected admin edit.",
        ),
    })
    .optional(),
  expectedRevision: zod
    .number()
    .min(approveRecipeDraftsBodyExpectedRevisionMin)
    .optional(),
});

export const ApproveRecipeDraftsResponse = zod.object({
  approved: zod.number(),
  skipped: zod.number(),
  details: zod.array(
    zod.object({
      id: zod.string(),
      reason: zod.string(),
    }),
  ),
  safety: zod.object({
    capturedAt: zod.string().nullable(),
    ratesMatch: zod.boolean(),
    quotesMatch: zod.boolean(),
    message: zod.string().nullable(),
    active: zod.boolean(),
  }),
});

export const GetRecipeDraftParams = zod.object({
  id: zod.coerce.string(),
});

export const getRecipeDraftResponseTwoDraftRecipeOneLabourAllowanceMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOnePlantAllowanceMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneSupplyAllowanceMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingReferenceQuantityExclusiveMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingDimensionLabelsMax = 3;

export const getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingFixedCostMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingReferencePriceMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingActualQuantityMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingResolvedQuantityMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneQuotePricingSelectedRateMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneQuotePricingBaselineMaterialsMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneQuotePricingBaselineLabourMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneQuotePricingBaselinePlantMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemBaseFactorMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemWastePctMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemQuantityPerUnitMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemUnitPriceMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemPackSizeExclusiveMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingFixedCostMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingReferencePriceMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingActualQuantityMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneMaterialsMax = 100;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemBaseFactorMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemWastePctMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemQuantityPerUnitMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemUnitPriceMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemPackSizeExclusiveMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingFixedCostMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingReferencePriceMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingActualQuantityMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsMax = 100;

export const GetRecipeDraftResponse = zod
  .object({
    id: zod.string(),
    name: zod.string(),
    description: zod.string().nullish(),
    notesIncluded: zod.string().nullish(),
    notesExcluded: zod.string().nullish(),
    reviewWarnings: zod.array(zod.string()).optional(),
    category: zod.string(),
    unit: zod.string(),
    currentTypical: zod.string(),
    hasLiveRecipe: zod.boolean(),
    draftId: zod.string().nullable(),
    generationState: zod
      .union([
        zod.literal("queued"),
        zod.literal("running"),
        zod.literal("done"),
        zod.literal("failed"),
        zod.literal(null),
      ])
      .nullable(),
    status: zod.enum([
      "not_drafted",
      "draft",
      "approved",
      "rejected",
      "redraft_requested",
    ]),
    note: zod.string(),
    ownerNote: zod.string(),
    revision: zod.number(),
    isStale: zod.boolean(),
    invalid: zod.boolean(),
    derivedTypical: zod.string().nullable(),
    sumDifference: zod.string().nullable(),
    usageRank: zod.number().nullable(),
  })
  .and(
    zod.object({
      draftRecipe: zod.union([
        zod.object({
          version: zod.literal(1),
          shape: zod.enum(["formula", "specification"]),
          labour_allowance: zod
            .number()
            .min(getRecipeDraftResponseTwoDraftRecipeOneLabourAllowanceMin),
          plant_allowance: zod
            .number()
            .min(getRecipeDraftResponseTwoDraftRecipeOnePlantAllowanceMin),
          specification: zod.string().optional(),
          supply_allowance: zod
            .number()
            .min(getRecipeDraftResponseTwoDraftRecipeOneSupplyAllowanceMin)
            .optional(),
          supply_name: zod.string().optional(),
          supply_material_id: zod.string().optional(),
          supply_pricing: zod
            .object({
              measure: zod.enum([
                "length",
                "area",
                "volume",
                "weight",
                "count",
              ]),
              reference_quantity: zod
                .number()
                .gt(
                  getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingReferenceQuantityExclusiveMin,
                ),
              dimension_labels: zod
                .array(zod.string())
                .min(1)
                .max(
                  getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingDimensionLabelsMax,
                ),
              fixed_cost: zod
                .number()
                .min(
                  getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingFixedCostMin,
                ),
              reference_price: zod
                .number()
                .min(
                  getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingReferencePriceMin,
                )
                .optional(),
              actual_quantity: zod
                .number()
                .min(
                  getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingActualQuantityMin,
                )
                .optional(),
              resolved_quantity: zod
                .number()
                .min(
                  getRecipeDraftResponseTwoDraftRecipeOneSupplyPricingResolvedQuantityMin,
                )
                .optional(),
              resolved_spec: zod.string().optional(),
              section_rates: zod
                .array(
                  zod.object({
                    section: zod.string(),
                    grade: zod.string(),
                    finish: zod.string(),
                    rate_per_metre: zod.number(),
                    supplier: zod.string(),
                    source_url: zod.string(),
                    checked_on: zod.string(),
                  }),
                )
                .optional(),
              length_label: zod.string().optional(),
              selected_section: zod.string().optional(),
              resolved_section: zod.string().optional(),
              evidence: zod.string(),
            })
            .optional(),
          quote_pricing: zod
            .object({
              selected_rate: zod
                .number()
                .min(
                  getRecipeDraftResponseTwoDraftRecipeOneQuotePricingSelectedRateMin,
                ),
              baseline_materials: zod
                .number()
                .min(
                  getRecipeDraftResponseTwoDraftRecipeOneQuotePricingBaselineMaterialsMin,
                ),
              baseline_labour: zod
                .number()
                .min(
                  getRecipeDraftResponseTwoDraftRecipeOneQuotePricingBaselineLabourMin,
                ),
              baseline_plant: zod
                .number()
                .min(
                  getRecipeDraftResponseTwoDraftRecipeOneQuotePricingBaselinePlantMin,
                ),
              status: zod.enum(["calculated", "needs_measurements", "manual"]),
              notes: zod.array(zod.string()),
            })
            .optional(),
          status: zod.enum(["none", "reviewed", "user_edited"]),
          materials: zod
            .array(
              zod.object({
                material_id: zod.string(),
                name: zod.string(),
                spec: zod.string().optional(),
                base_factor: zod
                  .number()
                  .min(
                    getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemBaseFactorMin,
                  ),
                waste_pct: zod
                  .number()
                  .min(
                    getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemWastePctMin,
                  ),
                quantity_per_unit: zod
                  .number()
                  .min(
                    getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemQuantityPerUnitMin,
                  )
                  .optional(),
                unit: zod.string(),
                unit_price: zod
                  .number()
                  .min(
                    getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemUnitPriceMin,
                  ),
                pack_size: zod
                  .number()
                  .gt(
                    getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemPackSizeExclusiveMin,
                  )
                  .optional(),
                round_packs: zod.boolean().optional(),
                assumption: zod.string(),
                dimension_pricing: zod
                  .object({
                    measure: zod.enum([
                      "length",
                      "area",
                      "volume",
                      "weight",
                      "count",
                    ]),
                    reference_quantity: zod
                      .number()
                      .gt(
                        getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                      ),
                    dimension_labels: zod
                      .array(zod.string())
                      .min(1)
                      .max(
                        getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingDimensionLabelsMax,
                      ),
                    fixed_cost: zod
                      .number()
                      .min(
                        getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingFixedCostMin,
                      ),
                    reference_price: zod
                      .number()
                      .min(
                        getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingReferencePriceMin,
                      )
                      .optional(),
                    actual_quantity: zod
                      .number()
                      .min(
                        getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingActualQuantityMin,
                      )
                      .optional(),
                    resolved_quantity: zod
                      .number()
                      .min(
                        getRecipeDraftResponseTwoDraftRecipeOneMaterialsItemDimensionPricingResolvedQuantityMin,
                      )
                      .optional(),
                    resolved_spec: zod.string().optional(),
                    section_rates: zod
                      .array(
                        zod.object({
                          section: zod.string(),
                          grade: zod.string(),
                          finish: zod.string(),
                          rate_per_metre: zod.number(),
                          supplier: zod.string(),
                          source_url: zod.string(),
                          checked_on: zod.string(),
                        }),
                      )
                      .optional(),
                    length_label: zod.string().optional(),
                    selected_section: zod.string().optional(),
                    resolved_section: zod.string().optional(),
                    evidence: zod.string(),
                  })
                  .optional(),
              }),
            )
            .max(getRecipeDraftResponseTwoDraftRecipeOneMaterialsMax),
          original_materials: zod
            .array(
              zod.object({
                material_id: zod.string(),
                name: zod.string(),
                spec: zod.string().optional(),
                base_factor: zod
                  .number()
                  .min(
                    getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemBaseFactorMin,
                  ),
                waste_pct: zod
                  .number()
                  .min(
                    getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemWastePctMin,
                  ),
                quantity_per_unit: zod
                  .number()
                  .min(
                    getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemQuantityPerUnitMin,
                  )
                  .optional(),
                unit: zod.string(),
                unit_price: zod
                  .number()
                  .min(
                    getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemUnitPriceMin,
                  ),
                pack_size: zod
                  .number()
                  .gt(
                    getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemPackSizeExclusiveMin,
                  )
                  .optional(),
                round_packs: zod.boolean().optional(),
                assumption: zod.string(),
                dimension_pricing: zod
                  .object({
                    measure: zod.enum([
                      "length",
                      "area",
                      "volume",
                      "weight",
                      "count",
                    ]),
                    reference_quantity: zod
                      .number()
                      .gt(
                        getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                      ),
                    dimension_labels: zod
                      .array(zod.string())
                      .min(1)
                      .max(
                        getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingDimensionLabelsMax,
                      ),
                    fixed_cost: zod
                      .number()
                      .min(
                        getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingFixedCostMin,
                      ),
                    reference_price: zod
                      .number()
                      .min(
                        getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingReferencePriceMin,
                      )
                      .optional(),
                    actual_quantity: zod
                      .number()
                      .min(
                        getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingActualQuantityMin,
                      )
                      .optional(),
                    resolved_quantity: zod
                      .number()
                      .min(
                        getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsItemDimensionPricingResolvedQuantityMin,
                      )
                      .optional(),
                    resolved_spec: zod.string().optional(),
                    section_rates: zod
                      .array(
                        zod.object({
                          section: zod.string(),
                          grade: zod.string(),
                          finish: zod.string(),
                          rate_per_metre: zod.number(),
                          supplier: zod.string(),
                          source_url: zod.string(),
                          checked_on: zod.string(),
                        }),
                      )
                      .optional(),
                    length_label: zod.string().optional(),
                    selected_section: zod.string().optional(),
                    resolved_section: zod.string().optional(),
                    evidence: zod.string(),
                  })
                  .optional(),
              }),
            )
            .max(getRecipeDraftResponseTwoDraftRecipeOneOriginalMaterialsMax)
            .optional()
            .describe(
              "Server-owned baseline captured before the first protected admin edit.",
            ),
        }),
        zod.null(),
      ]),
    }),
  );

export const SaveRecipeDraftParams = zod.object({
  id: zod.coerce.string(),
});

export const saveRecipeDraftBodyRecipeLabourAllowanceMin = 0;

export const saveRecipeDraftBodyRecipePlantAllowanceMin = 0;

export const saveRecipeDraftBodyRecipeSupplyAllowanceMin = 0;

export const saveRecipeDraftBodyRecipeSupplyPricingReferenceQuantityExclusiveMin = 0;

export const saveRecipeDraftBodyRecipeSupplyPricingDimensionLabelsMax = 3;

export const saveRecipeDraftBodyRecipeSupplyPricingFixedCostMin = 0;

export const saveRecipeDraftBodyRecipeSupplyPricingReferencePriceMin = 0;

export const saveRecipeDraftBodyRecipeSupplyPricingActualQuantityMin = 0;

export const saveRecipeDraftBodyRecipeSupplyPricingResolvedQuantityMin = 0;

export const saveRecipeDraftBodyRecipeQuotePricingSelectedRateMin = 0;

export const saveRecipeDraftBodyRecipeQuotePricingBaselineMaterialsMin = 0;

export const saveRecipeDraftBodyRecipeQuotePricingBaselineLabourMin = 0;

export const saveRecipeDraftBodyRecipeQuotePricingBaselinePlantMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemBaseFactorMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemWastePctMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemQuantityPerUnitMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemUnitPriceMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemPackSizeExclusiveMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingFixedCostMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingReferencePriceMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingActualQuantityMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const saveRecipeDraftBodyRecipeMaterialsMax = 100;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemBaseFactorMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemWastePctMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemQuantityPerUnitMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemUnitPriceMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemPackSizeExclusiveMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingFixedCostMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingReferencePriceMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingActualQuantityMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const saveRecipeDraftBodyRecipeOriginalMaterialsMax = 100;

export const saveRecipeDraftBodyRevisionMin = 0;

export const SaveRecipeDraftBody = zod.object({
  recipe: zod.object({
    version: zod.literal(1),
    shape: zod.enum(["formula", "specification"]),
    labour_allowance: zod
      .number()
      .min(saveRecipeDraftBodyRecipeLabourAllowanceMin),
    plant_allowance: zod
      .number()
      .min(saveRecipeDraftBodyRecipePlantAllowanceMin),
    specification: zod.string().optional(),
    supply_allowance: zod
      .number()
      .min(saveRecipeDraftBodyRecipeSupplyAllowanceMin)
      .optional(),
    supply_name: zod.string().optional(),
    supply_material_id: zod.string().optional(),
    supply_pricing: zod
      .object({
        measure: zod.enum(["length", "area", "volume", "weight", "count"]),
        reference_quantity: zod
          .number()
          .gt(
            saveRecipeDraftBodyRecipeSupplyPricingReferenceQuantityExclusiveMin,
          ),
        dimension_labels: zod
          .array(zod.string())
          .min(1)
          .max(saveRecipeDraftBodyRecipeSupplyPricingDimensionLabelsMax),
        fixed_cost: zod
          .number()
          .min(saveRecipeDraftBodyRecipeSupplyPricingFixedCostMin),
        reference_price: zod
          .number()
          .min(saveRecipeDraftBodyRecipeSupplyPricingReferencePriceMin)
          .optional(),
        actual_quantity: zod
          .number()
          .min(saveRecipeDraftBodyRecipeSupplyPricingActualQuantityMin)
          .optional(),
        resolved_quantity: zod
          .number()
          .min(saveRecipeDraftBodyRecipeSupplyPricingResolvedQuantityMin)
          .optional(),
        resolved_spec: zod.string().optional(),
        section_rates: zod
          .array(
            zod.object({
              section: zod.string(),
              grade: zod.string(),
              finish: zod.string(),
              rate_per_metre: zod.number(),
              supplier: zod.string(),
              source_url: zod.string(),
              checked_on: zod.string(),
            }),
          )
          .optional(),
        length_label: zod.string().optional(),
        selected_section: zod.string().optional(),
        resolved_section: zod.string().optional(),
        evidence: zod.string(),
      })
      .optional(),
    quote_pricing: zod
      .object({
        selected_rate: zod
          .number()
          .min(saveRecipeDraftBodyRecipeQuotePricingSelectedRateMin),
        baseline_materials: zod
          .number()
          .min(saveRecipeDraftBodyRecipeQuotePricingBaselineMaterialsMin),
        baseline_labour: zod
          .number()
          .min(saveRecipeDraftBodyRecipeQuotePricingBaselineLabourMin),
        baseline_plant: zod
          .number()
          .min(saveRecipeDraftBodyRecipeQuotePricingBaselinePlantMin),
        status: zod.enum(["calculated", "needs_measurements", "manual"]),
        notes: zod.array(zod.string()),
      })
      .optional(),
    status: zod.enum(["none", "reviewed", "user_edited"]),
    materials: zod
      .array(
        zod.object({
          material_id: zod.string(),
          name: zod.string(),
          spec: zod.string().optional(),
          base_factor: zod
            .number()
            .min(saveRecipeDraftBodyRecipeMaterialsItemBaseFactorMin),
          waste_pct: zod
            .number()
            .min(saveRecipeDraftBodyRecipeMaterialsItemWastePctMin),
          quantity_per_unit: zod
            .number()
            .min(saveRecipeDraftBodyRecipeMaterialsItemQuantityPerUnitMin)
            .optional(),
          unit: zod.string(),
          unit_price: zod
            .number()
            .min(saveRecipeDraftBodyRecipeMaterialsItemUnitPriceMin),
          pack_size: zod
            .number()
            .gt(saveRecipeDraftBodyRecipeMaterialsItemPackSizeExclusiveMin)
            .optional(),
          round_packs: zod.boolean().optional(),
          assumption: zod.string(),
          dimension_pricing: zod
            .object({
              measure: zod.enum([
                "length",
                "area",
                "volume",
                "weight",
                "count",
              ]),
              reference_quantity: zod
                .number()
                .gt(
                  saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                ),
              dimension_labels: zod
                .array(zod.string())
                .min(1)
                .max(
                  saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingDimensionLabelsMax,
                ),
              fixed_cost: zod
                .number()
                .min(
                  saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingFixedCostMin,
                ),
              reference_price: zod
                .number()
                .min(
                  saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingReferencePriceMin,
                )
                .optional(),
              actual_quantity: zod
                .number()
                .min(
                  saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingActualQuantityMin,
                )
                .optional(),
              resolved_quantity: zod
                .number()
                .min(
                  saveRecipeDraftBodyRecipeMaterialsItemDimensionPricingResolvedQuantityMin,
                )
                .optional(),
              resolved_spec: zod.string().optional(),
              section_rates: zod
                .array(
                  zod.object({
                    section: zod.string(),
                    grade: zod.string(),
                    finish: zod.string(),
                    rate_per_metre: zod.number(),
                    supplier: zod.string(),
                    source_url: zod.string(),
                    checked_on: zod.string(),
                  }),
                )
                .optional(),
              length_label: zod.string().optional(),
              selected_section: zod.string().optional(),
              resolved_section: zod.string().optional(),
              evidence: zod.string(),
            })
            .optional(),
        }),
      )
      .max(saveRecipeDraftBodyRecipeMaterialsMax),
    original_materials: zod
      .array(
        zod.object({
          material_id: zod.string(),
          name: zod.string(),
          spec: zod.string().optional(),
          base_factor: zod
            .number()
            .min(saveRecipeDraftBodyRecipeOriginalMaterialsItemBaseFactorMin),
          waste_pct: zod
            .number()
            .min(saveRecipeDraftBodyRecipeOriginalMaterialsItemWastePctMin),
          quantity_per_unit: zod
            .number()
            .min(
              saveRecipeDraftBodyRecipeOriginalMaterialsItemQuantityPerUnitMin,
            )
            .optional(),
          unit: zod.string(),
          unit_price: zod
            .number()
            .min(saveRecipeDraftBodyRecipeOriginalMaterialsItemUnitPriceMin),
          pack_size: zod
            .number()
            .gt(
              saveRecipeDraftBodyRecipeOriginalMaterialsItemPackSizeExclusiveMin,
            )
            .optional(),
          round_packs: zod.boolean().optional(),
          assumption: zod.string(),
          dimension_pricing: zod
            .object({
              measure: zod.enum([
                "length",
                "area",
                "volume",
                "weight",
                "count",
              ]),
              reference_quantity: zod
                .number()
                .gt(
                  saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                ),
              dimension_labels: zod
                .array(zod.string())
                .min(1)
                .max(
                  saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingDimensionLabelsMax,
                ),
              fixed_cost: zod
                .number()
                .min(
                  saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingFixedCostMin,
                ),
              reference_price: zod
                .number()
                .min(
                  saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingReferencePriceMin,
                )
                .optional(),
              actual_quantity: zod
                .number()
                .min(
                  saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingActualQuantityMin,
                )
                .optional(),
              resolved_quantity: zod
                .number()
                .min(
                  saveRecipeDraftBodyRecipeOriginalMaterialsItemDimensionPricingResolvedQuantityMin,
                )
                .optional(),
              resolved_spec: zod.string().optional(),
              section_rates: zod
                .array(
                  zod.object({
                    section: zod.string(),
                    grade: zod.string(),
                    finish: zod.string(),
                    rate_per_metre: zod.number(),
                    supplier: zod.string(),
                    source_url: zod.string(),
                    checked_on: zod.string(),
                  }),
                )
                .optional(),
              length_label: zod.string().optional(),
              selected_section: zod.string().optional(),
              resolved_section: zod.string().optional(),
              evidence: zod.string(),
            })
            .optional(),
        }),
      )
      .max(saveRecipeDraftBodyRecipeOriginalMaterialsMax)
      .optional()
      .describe(
        "Server-owned baseline captured before the first protected admin edit.",
      ),
  }),
  revision: zod.number().min(saveRecipeDraftBodyRevisionMin),
});

export const SaveRecipeDraftResponse = zod.object({
  saved: zod.boolean(),
});

export const RedraftCostRecipeParams = zod.object({
  id: zod.coerce.string(),
});

export const redraftCostRecipeBodyNoteMax = 2000;

export const RedraftCostRecipeBody = zod.object({
  note: zod.string().max(redraftCostRecipeBodyNoteMax).optional(),
});

export const RedraftCostRecipeResponse = zod.object({
  saved: zod.boolean(),
});

export const SkipRecipeDraftParams = zod.object({
  id: zod.coerce.string(),
});

export const skipRecipeDraftBodyRevisionMin = 0;

export const SkipRecipeDraftBody = zod.object({
  revision: zod.number().min(skipRecipeDraftBodyRevisionMin),
});

export const SkipRecipeDraftResponse = zod.object({
  saved: zod.boolean(),
});

export const GetCostRecipeParams = zod.object({
  source: zod.enum(["default", "custom"]),
  id: zod.coerce.string(),
});

export const getCostRecipeResponseRecipeOneLabourAllowanceMin = 0;

export const getCostRecipeResponseRecipeOnePlantAllowanceMin = 0;

export const getCostRecipeResponseRecipeOneSupplyAllowanceMin = 0;

export const getCostRecipeResponseRecipeOneSupplyPricingReferenceQuantityExclusiveMin = 0;

export const getCostRecipeResponseRecipeOneSupplyPricingDimensionLabelsMax = 3;

export const getCostRecipeResponseRecipeOneSupplyPricingFixedCostMin = 0;

export const getCostRecipeResponseRecipeOneSupplyPricingReferencePriceMin = 0;

export const getCostRecipeResponseRecipeOneSupplyPricingActualQuantityMin = 0;

export const getCostRecipeResponseRecipeOneSupplyPricingResolvedQuantityMin = 0;

export const getCostRecipeResponseRecipeOneQuotePricingSelectedRateMin = 0;

export const getCostRecipeResponseRecipeOneQuotePricingBaselineMaterialsMin = 0;

export const getCostRecipeResponseRecipeOneQuotePricingBaselineLabourMin = 0;

export const getCostRecipeResponseRecipeOneQuotePricingBaselinePlantMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemBaseFactorMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemWastePctMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemQuantityPerUnitMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemUnitPriceMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemPackSizeExclusiveMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingFixedCostMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferencePriceMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingActualQuantityMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const getCostRecipeResponseRecipeOneMaterialsMax = 100;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemBaseFactorMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemWastePctMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemQuantityPerUnitMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemUnitPriceMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemPackSizeExclusiveMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingFixedCostMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferencePriceMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingActualQuantityMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const getCostRecipeResponseRecipeOneOriginalMaterialsMax = 100;

export const GetCostRecipeResponse = zod.object({
  hasOriginalMaterials: zod
    .boolean()
    .optional()
    .describe(
      "Protected original materials are available for an administrator to restore. Their rows are not part of the pricing recipe.",
    ),
  recipe: zod.union([
    zod.object({
      version: zod.literal(1),
      shape: zod.enum(["formula", "specification"]),
      labour_allowance: zod
        .number()
        .min(getCostRecipeResponseRecipeOneLabourAllowanceMin),
      plant_allowance: zod
        .number()
        .min(getCostRecipeResponseRecipeOnePlantAllowanceMin),
      specification: zod.string().optional(),
      supply_allowance: zod
        .number()
        .min(getCostRecipeResponseRecipeOneSupplyAllowanceMin)
        .optional(),
      supply_name: zod.string().optional(),
      supply_material_id: zod.string().optional(),
      supply_pricing: zod
        .object({
          measure: zod.enum(["length", "area", "volume", "weight", "count"]),
          reference_quantity: zod
            .number()
            .gt(
              getCostRecipeResponseRecipeOneSupplyPricingReferenceQuantityExclusiveMin,
            ),
          dimension_labels: zod
            .array(zod.string())
            .min(1)
            .max(getCostRecipeResponseRecipeOneSupplyPricingDimensionLabelsMax),
          fixed_cost: zod
            .number()
            .min(getCostRecipeResponseRecipeOneSupplyPricingFixedCostMin),
          reference_price: zod
            .number()
            .min(getCostRecipeResponseRecipeOneSupplyPricingReferencePriceMin)
            .optional(),
          actual_quantity: zod
            .number()
            .min(getCostRecipeResponseRecipeOneSupplyPricingActualQuantityMin)
            .optional(),
          resolved_quantity: zod
            .number()
            .min(getCostRecipeResponseRecipeOneSupplyPricingResolvedQuantityMin)
            .optional(),
          resolved_spec: zod.string().optional(),
          section_rates: zod
            .array(
              zod.object({
                section: zod.string(),
                grade: zod.string(),
                finish: zod.string(),
                rate_per_metre: zod.number(),
                supplier: zod.string(),
                source_url: zod.string(),
                checked_on: zod.string(),
              }),
            )
            .optional(),
          length_label: zod.string().optional(),
          selected_section: zod.string().optional(),
          resolved_section: zod.string().optional(),
          evidence: zod.string(),
        })
        .optional(),
      quote_pricing: zod
        .object({
          selected_rate: zod
            .number()
            .min(getCostRecipeResponseRecipeOneQuotePricingSelectedRateMin),
          baseline_materials: zod
            .number()
            .min(
              getCostRecipeResponseRecipeOneQuotePricingBaselineMaterialsMin,
            ),
          baseline_labour: zod
            .number()
            .min(getCostRecipeResponseRecipeOneQuotePricingBaselineLabourMin),
          baseline_plant: zod
            .number()
            .min(getCostRecipeResponseRecipeOneQuotePricingBaselinePlantMin),
          status: zod.enum(["calculated", "needs_measurements", "manual"]),
          notes: zod.array(zod.string()),
        })
        .optional(),
      status: zod.enum(["none", "reviewed", "user_edited"]),
      materials: zod
        .array(
          zod.object({
            material_id: zod.string(),
            name: zod.string(),
            spec: zod.string().optional(),
            base_factor: zod
              .number()
              .min(getCostRecipeResponseRecipeOneMaterialsItemBaseFactorMin),
            waste_pct: zod
              .number()
              .min(getCostRecipeResponseRecipeOneMaterialsItemWastePctMin),
            quantity_per_unit: zod
              .number()
              .min(
                getCostRecipeResponseRecipeOneMaterialsItemQuantityPerUnitMin,
              )
              .optional(),
            unit: zod.string(),
            unit_price: zod
              .number()
              .min(getCostRecipeResponseRecipeOneMaterialsItemUnitPriceMin),
            pack_size: zod
              .number()
              .gt(
                getCostRecipeResponseRecipeOneMaterialsItemPackSizeExclusiveMin,
              )
              .optional(),
            round_packs: zod.boolean().optional(),
            assumption: zod.string(),
            dimension_pricing: zod
              .object({
                measure: zod.enum([
                  "length",
                  "area",
                  "volume",
                  "weight",
                  "count",
                ]),
                reference_quantity: zod
                  .number()
                  .gt(
                    getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                  ),
                dimension_labels: zod
                  .array(zod.string())
                  .min(1)
                  .max(
                    getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingDimensionLabelsMax,
                  ),
                fixed_cost: zod
                  .number()
                  .min(
                    getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingFixedCostMin,
                  ),
                reference_price: zod
                  .number()
                  .min(
                    getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferencePriceMin,
                  )
                  .optional(),
                actual_quantity: zod
                  .number()
                  .min(
                    getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingActualQuantityMin,
                  )
                  .optional(),
                resolved_quantity: zod
                  .number()
                  .min(
                    getCostRecipeResponseRecipeOneMaterialsItemDimensionPricingResolvedQuantityMin,
                  )
                  .optional(),
                resolved_spec: zod.string().optional(),
                section_rates: zod
                  .array(
                    zod.object({
                      section: zod.string(),
                      grade: zod.string(),
                      finish: zod.string(),
                      rate_per_metre: zod.number(),
                      supplier: zod.string(),
                      source_url: zod.string(),
                      checked_on: zod.string(),
                    }),
                  )
                  .optional(),
                length_label: zod.string().optional(),
                selected_section: zod.string().optional(),
                resolved_section: zod.string().optional(),
                evidence: zod.string(),
              })
              .optional(),
          }),
        )
        .max(getCostRecipeResponseRecipeOneMaterialsMax),
      original_materials: zod
        .array(
          zod.object({
            material_id: zod.string(),
            name: zod.string(),
            spec: zod.string().optional(),
            base_factor: zod
              .number()
              .min(
                getCostRecipeResponseRecipeOneOriginalMaterialsItemBaseFactorMin,
              ),
            waste_pct: zod
              .number()
              .min(
                getCostRecipeResponseRecipeOneOriginalMaterialsItemWastePctMin,
              ),
            quantity_per_unit: zod
              .number()
              .min(
                getCostRecipeResponseRecipeOneOriginalMaterialsItemQuantityPerUnitMin,
              )
              .optional(),
            unit: zod.string(),
            unit_price: zod
              .number()
              .min(
                getCostRecipeResponseRecipeOneOriginalMaterialsItemUnitPriceMin,
              ),
            pack_size: zod
              .number()
              .gt(
                getCostRecipeResponseRecipeOneOriginalMaterialsItemPackSizeExclusiveMin,
              )
              .optional(),
            round_packs: zod.boolean().optional(),
            assumption: zod.string(),
            dimension_pricing: zod
              .object({
                measure: zod.enum([
                  "length",
                  "area",
                  "volume",
                  "weight",
                  "count",
                ]),
                reference_quantity: zod
                  .number()
                  .gt(
                    getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                  ),
                dimension_labels: zod
                  .array(zod.string())
                  .min(1)
                  .max(
                    getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingDimensionLabelsMax,
                  ),
                fixed_cost: zod
                  .number()
                  .min(
                    getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingFixedCostMin,
                  ),
                reference_price: zod
                  .number()
                  .min(
                    getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferencePriceMin,
                  )
                  .optional(),
                actual_quantity: zod
                  .number()
                  .min(
                    getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingActualQuantityMin,
                  )
                  .optional(),
                resolved_quantity: zod
                  .number()
                  .min(
                    getCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingResolvedQuantityMin,
                  )
                  .optional(),
                resolved_spec: zod.string().optional(),
                section_rates: zod
                  .array(
                    zod.object({
                      section: zod.string(),
                      grade: zod.string(),
                      finish: zod.string(),
                      rate_per_metre: zod.number(),
                      supplier: zod.string(),
                      source_url: zod.string(),
                      checked_on: zod.string(),
                    }),
                  )
                  .optional(),
                length_label: zod.string().optional(),
                selected_section: zod.string().optional(),
                resolved_section: zod.string().optional(),
                evidence: zod.string(),
              })
              .optional(),
          }),
        )
        .max(getCostRecipeResponseRecipeOneOriginalMaterialsMax)
        .optional()
        .describe(
          "Server-owned baseline captured before the first protected admin edit.",
        ),
    }),
    zod.null(),
  ]),
  rateTypical: zod.string(),
  rateLow: zod.string().optional(),
  rateHigh: zod.string().optional(),
  unit: zod.string(),
  recipeSource: zod
    .union([
      zod.literal("default"),
      zod.literal("override"),
      zod.literal("custom"),
      zod.literal(null),
    ])
    .nullable(),
  hasRecipeOverride: zod.boolean(),
  legacyOverride: zod.boolean(),
});

export const SaveCostRecipeParams = zod.object({
  source: zod.enum(["default", "custom"]),
  id: zod.coerce.string(),
});

export const saveCostRecipeBodyRecipeLabourAllowanceMin = 0;

export const saveCostRecipeBodyRecipePlantAllowanceMin = 0;

export const saveCostRecipeBodyRecipeSupplyAllowanceMin = 0;

export const saveCostRecipeBodyRecipeSupplyPricingReferenceQuantityExclusiveMin = 0;

export const saveCostRecipeBodyRecipeSupplyPricingDimensionLabelsMax = 3;

export const saveCostRecipeBodyRecipeSupplyPricingFixedCostMin = 0;

export const saveCostRecipeBodyRecipeSupplyPricingReferencePriceMin = 0;

export const saveCostRecipeBodyRecipeSupplyPricingActualQuantityMin = 0;

export const saveCostRecipeBodyRecipeSupplyPricingResolvedQuantityMin = 0;

export const saveCostRecipeBodyRecipeQuotePricingSelectedRateMin = 0;

export const saveCostRecipeBodyRecipeQuotePricingBaselineMaterialsMin = 0;

export const saveCostRecipeBodyRecipeQuotePricingBaselineLabourMin = 0;

export const saveCostRecipeBodyRecipeQuotePricingBaselinePlantMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemBaseFactorMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemWastePctMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemQuantityPerUnitMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemUnitPriceMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemPackSizeExclusiveMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const saveCostRecipeBodyRecipeMaterialsItemDimensionPricingFixedCostMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemDimensionPricingReferencePriceMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemDimensionPricingActualQuantityMin = 0;

export const saveCostRecipeBodyRecipeMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const saveCostRecipeBodyRecipeMaterialsMax = 100;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemBaseFactorMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemWastePctMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemQuantityPerUnitMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemUnitPriceMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemPackSizeExclusiveMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingFixedCostMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingReferencePriceMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingActualQuantityMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const saveCostRecipeBodyRecipeOriginalMaterialsMax = 100;

export const saveCostRecipeBodyRateAdjustmentChangeReasonMax = 2000;

export const SaveCostRecipeBody = zod
  .object({
    recipe: zod
      .object({
        version: zod.literal(1),
        shape: zod.enum(["formula", "specification"]),
        labour_allowance: zod
          .number()
          .min(saveCostRecipeBodyRecipeLabourAllowanceMin),
        plant_allowance: zod
          .number()
          .min(saveCostRecipeBodyRecipePlantAllowanceMin),
        specification: zod.string().optional(),
        supply_allowance: zod
          .number()
          .min(saveCostRecipeBodyRecipeSupplyAllowanceMin)
          .optional(),
        supply_name: zod.string().optional(),
        supply_material_id: zod.string().optional(),
        supply_pricing: zod
          .object({
            measure: zod.enum(["length", "area", "volume", "weight", "count"]),
            reference_quantity: zod
              .number()
              .gt(
                saveCostRecipeBodyRecipeSupplyPricingReferenceQuantityExclusiveMin,
              ),
            dimension_labels: zod
              .array(zod.string())
              .min(1)
              .max(saveCostRecipeBodyRecipeSupplyPricingDimensionLabelsMax),
            fixed_cost: zod
              .number()
              .min(saveCostRecipeBodyRecipeSupplyPricingFixedCostMin),
            reference_price: zod
              .number()
              .min(saveCostRecipeBodyRecipeSupplyPricingReferencePriceMin)
              .optional(),
            actual_quantity: zod
              .number()
              .min(saveCostRecipeBodyRecipeSupplyPricingActualQuantityMin)
              .optional(),
            resolved_quantity: zod
              .number()
              .min(saveCostRecipeBodyRecipeSupplyPricingResolvedQuantityMin)
              .optional(),
            resolved_spec: zod.string().optional(),
            section_rates: zod
              .array(
                zod.object({
                  section: zod.string(),
                  grade: zod.string(),
                  finish: zod.string(),
                  rate_per_metre: zod.number(),
                  supplier: zod.string(),
                  source_url: zod.string(),
                  checked_on: zod.string(),
                }),
              )
              .optional(),
            length_label: zod.string().optional(),
            selected_section: zod.string().optional(),
            resolved_section: zod.string().optional(),
            evidence: zod.string(),
          })
          .optional(),
        quote_pricing: zod
          .object({
            selected_rate: zod
              .number()
              .min(saveCostRecipeBodyRecipeQuotePricingSelectedRateMin),
            baseline_materials: zod
              .number()
              .min(saveCostRecipeBodyRecipeQuotePricingBaselineMaterialsMin),
            baseline_labour: zod
              .number()
              .min(saveCostRecipeBodyRecipeQuotePricingBaselineLabourMin),
            baseline_plant: zod
              .number()
              .min(saveCostRecipeBodyRecipeQuotePricingBaselinePlantMin),
            status: zod.enum(["calculated", "needs_measurements", "manual"]),
            notes: zod.array(zod.string()),
          })
          .optional(),
        status: zod.enum(["none", "reviewed", "user_edited"]),
        materials: zod
          .array(
            zod.object({
              material_id: zod.string(),
              name: zod.string(),
              spec: zod.string().optional(),
              base_factor: zod
                .number()
                .min(saveCostRecipeBodyRecipeMaterialsItemBaseFactorMin),
              waste_pct: zod
                .number()
                .min(saveCostRecipeBodyRecipeMaterialsItemWastePctMin),
              quantity_per_unit: zod
                .number()
                .min(saveCostRecipeBodyRecipeMaterialsItemQuantityPerUnitMin)
                .optional(),
              unit: zod.string(),
              unit_price: zod
                .number()
                .min(saveCostRecipeBodyRecipeMaterialsItemUnitPriceMin),
              pack_size: zod
                .number()
                .gt(saveCostRecipeBodyRecipeMaterialsItemPackSizeExclusiveMin)
                .optional(),
              round_packs: zod.boolean().optional(),
              assumption: zod.string(),
              dimension_pricing: zod
                .object({
                  measure: zod.enum([
                    "length",
                    "area",
                    "volume",
                    "weight",
                    "count",
                  ]),
                  reference_quantity: zod
                    .number()
                    .gt(
                      saveCostRecipeBodyRecipeMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                    ),
                  dimension_labels: zod
                    .array(zod.string())
                    .min(1)
                    .max(
                      saveCostRecipeBodyRecipeMaterialsItemDimensionPricingDimensionLabelsMax,
                    ),
                  fixed_cost: zod
                    .number()
                    .min(
                      saveCostRecipeBodyRecipeMaterialsItemDimensionPricingFixedCostMin,
                    ),
                  reference_price: zod
                    .number()
                    .min(
                      saveCostRecipeBodyRecipeMaterialsItemDimensionPricingReferencePriceMin,
                    )
                    .optional(),
                  actual_quantity: zod
                    .number()
                    .min(
                      saveCostRecipeBodyRecipeMaterialsItemDimensionPricingActualQuantityMin,
                    )
                    .optional(),
                  resolved_quantity: zod
                    .number()
                    .min(
                      saveCostRecipeBodyRecipeMaterialsItemDimensionPricingResolvedQuantityMin,
                    )
                    .optional(),
                  resolved_spec: zod.string().optional(),
                  section_rates: zod
                    .array(
                      zod.object({
                        section: zod.string(),
                        grade: zod.string(),
                        finish: zod.string(),
                        rate_per_metre: zod.number(),
                        supplier: zod.string(),
                        source_url: zod.string(),
                        checked_on: zod.string(),
                      }),
                    )
                    .optional(),
                  length_label: zod.string().optional(),
                  selected_section: zod.string().optional(),
                  resolved_section: zod.string().optional(),
                  evidence: zod.string(),
                })
                .optional(),
            }),
          )
          .max(saveCostRecipeBodyRecipeMaterialsMax),
        original_materials: zod
          .array(
            zod.object({
              material_id: zod.string(),
              name: zod.string(),
              spec: zod.string().optional(),
              base_factor: zod
                .number()
                .min(
                  saveCostRecipeBodyRecipeOriginalMaterialsItemBaseFactorMin,
                ),
              waste_pct: zod
                .number()
                .min(saveCostRecipeBodyRecipeOriginalMaterialsItemWastePctMin),
              quantity_per_unit: zod
                .number()
                .min(
                  saveCostRecipeBodyRecipeOriginalMaterialsItemQuantityPerUnitMin,
                )
                .optional(),
              unit: zod.string(),
              unit_price: zod
                .number()
                .min(saveCostRecipeBodyRecipeOriginalMaterialsItemUnitPriceMin),
              pack_size: zod
                .number()
                .gt(
                  saveCostRecipeBodyRecipeOriginalMaterialsItemPackSizeExclusiveMin,
                )
                .optional(),
              round_packs: zod.boolean().optional(),
              assumption: zod.string(),
              dimension_pricing: zod
                .object({
                  measure: zod.enum([
                    "length",
                    "area",
                    "volume",
                    "weight",
                    "count",
                  ]),
                  reference_quantity: zod
                    .number()
                    .gt(
                      saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                    ),
                  dimension_labels: zod
                    .array(zod.string())
                    .min(1)
                    .max(
                      saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingDimensionLabelsMax,
                    ),
                  fixed_cost: zod
                    .number()
                    .min(
                      saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingFixedCostMin,
                    ),
                  reference_price: zod
                    .number()
                    .min(
                      saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingReferencePriceMin,
                    )
                    .optional(),
                  actual_quantity: zod
                    .number()
                    .min(
                      saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingActualQuantityMin,
                    )
                    .optional(),
                  resolved_quantity: zod
                    .number()
                    .min(
                      saveCostRecipeBodyRecipeOriginalMaterialsItemDimensionPricingResolvedQuantityMin,
                    )
                    .optional(),
                  resolved_spec: zod.string().optional(),
                  section_rates: zod
                    .array(
                      zod.object({
                        section: zod.string(),
                        grade: zod.string(),
                        finish: zod.string(),
                        rate_per_metre: zod.number(),
                        supplier: zod.string(),
                        source_url: zod.string(),
                        checked_on: zod.string(),
                      }),
                    )
                    .optional(),
                  length_label: zod.string().optional(),
                  selected_section: zod.string().optional(),
                  resolved_section: zod.string().optional(),
                  evidence: zod.string(),
                })
                .optional(),
            }),
          )
          .max(saveCostRecipeBodyRecipeOriginalMaterialsMax)
          .optional()
          .describe(
            "Server-owned baseline captured before the first protected admin edit.",
          ),
      })
      .optional(),
    expectedSnapshot: zod.string().optional(),
    rateAdjustment: zod
      .object({
        rateLow: zod.string(),
        rateTypical: zod.string(),
        rateHigh: zod.string(),
        expectedSnapshot: zod.string(),
        changeReason: zod
          .string()
          .max(saveCostRecipeBodyRateAdjustmentChangeReasonMax)
          .optional(),
      })
      .optional(),
    restoreMaterials: zod.literal(true).optional(),
  })
  .describe(
    "Supply a whole recipe, a confirmed direct rate adjustment, or admin-only restoreMaterials with expectedSnapshot. Never combine actions.",
  );

export const saveCostRecipeResponseRecipeOneLabourAllowanceMin = 0;

export const saveCostRecipeResponseRecipeOnePlantAllowanceMin = 0;

export const saveCostRecipeResponseRecipeOneSupplyAllowanceMin = 0;

export const saveCostRecipeResponseRecipeOneSupplyPricingReferenceQuantityExclusiveMin = 0;

export const saveCostRecipeResponseRecipeOneSupplyPricingDimensionLabelsMax = 3;

export const saveCostRecipeResponseRecipeOneSupplyPricingFixedCostMin = 0;

export const saveCostRecipeResponseRecipeOneSupplyPricingReferencePriceMin = 0;

export const saveCostRecipeResponseRecipeOneSupplyPricingActualQuantityMin = 0;

export const saveCostRecipeResponseRecipeOneSupplyPricingResolvedQuantityMin = 0;

export const saveCostRecipeResponseRecipeOneQuotePricingSelectedRateMin = 0;

export const saveCostRecipeResponseRecipeOneQuotePricingBaselineMaterialsMin = 0;

export const saveCostRecipeResponseRecipeOneQuotePricingBaselineLabourMin = 0;

export const saveCostRecipeResponseRecipeOneQuotePricingBaselinePlantMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemBaseFactorMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemWastePctMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemQuantityPerUnitMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemUnitPriceMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemPackSizeExclusiveMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingFixedCostMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferencePriceMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingActualQuantityMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const saveCostRecipeResponseRecipeOneMaterialsMax = 100;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemBaseFactorMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemWastePctMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemQuantityPerUnitMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemUnitPriceMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemPackSizeExclusiveMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingFixedCostMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferencePriceMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingActualQuantityMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const saveCostRecipeResponseRecipeOneOriginalMaterialsMax = 100;

export const SaveCostRecipeResponse = zod.object({
  hasOriginalMaterials: zod
    .boolean()
    .optional()
    .describe(
      "Protected original materials are available for an administrator to restore. Their rows are not part of the pricing recipe.",
    ),
  recipe: zod.union([
    zod.object({
      version: zod.literal(1),
      shape: zod.enum(["formula", "specification"]),
      labour_allowance: zod
        .number()
        .min(saveCostRecipeResponseRecipeOneLabourAllowanceMin),
      plant_allowance: zod
        .number()
        .min(saveCostRecipeResponseRecipeOnePlantAllowanceMin),
      specification: zod.string().optional(),
      supply_allowance: zod
        .number()
        .min(saveCostRecipeResponseRecipeOneSupplyAllowanceMin)
        .optional(),
      supply_name: zod.string().optional(),
      supply_material_id: zod.string().optional(),
      supply_pricing: zod
        .object({
          measure: zod.enum(["length", "area", "volume", "weight", "count"]),
          reference_quantity: zod
            .number()
            .gt(
              saveCostRecipeResponseRecipeOneSupplyPricingReferenceQuantityExclusiveMin,
            ),
          dimension_labels: zod
            .array(zod.string())
            .min(1)
            .max(
              saveCostRecipeResponseRecipeOneSupplyPricingDimensionLabelsMax,
            ),
          fixed_cost: zod
            .number()
            .min(saveCostRecipeResponseRecipeOneSupplyPricingFixedCostMin),
          reference_price: zod
            .number()
            .min(saveCostRecipeResponseRecipeOneSupplyPricingReferencePriceMin)
            .optional(),
          actual_quantity: zod
            .number()
            .min(saveCostRecipeResponseRecipeOneSupplyPricingActualQuantityMin)
            .optional(),
          resolved_quantity: zod
            .number()
            .min(
              saveCostRecipeResponseRecipeOneSupplyPricingResolvedQuantityMin,
            )
            .optional(),
          resolved_spec: zod.string().optional(),
          section_rates: zod
            .array(
              zod.object({
                section: zod.string(),
                grade: zod.string(),
                finish: zod.string(),
                rate_per_metre: zod.number(),
                supplier: zod.string(),
                source_url: zod.string(),
                checked_on: zod.string(),
              }),
            )
            .optional(),
          length_label: zod.string().optional(),
          selected_section: zod.string().optional(),
          resolved_section: zod.string().optional(),
          evidence: zod.string(),
        })
        .optional(),
      quote_pricing: zod
        .object({
          selected_rate: zod
            .number()
            .min(saveCostRecipeResponseRecipeOneQuotePricingSelectedRateMin),
          baseline_materials: zod
            .number()
            .min(
              saveCostRecipeResponseRecipeOneQuotePricingBaselineMaterialsMin,
            ),
          baseline_labour: zod
            .number()
            .min(saveCostRecipeResponseRecipeOneQuotePricingBaselineLabourMin),
          baseline_plant: zod
            .number()
            .min(saveCostRecipeResponseRecipeOneQuotePricingBaselinePlantMin),
          status: zod.enum(["calculated", "needs_measurements", "manual"]),
          notes: zod.array(zod.string()),
        })
        .optional(),
      status: zod.enum(["none", "reviewed", "user_edited"]),
      materials: zod
        .array(
          zod.object({
            material_id: zod.string(),
            name: zod.string(),
            spec: zod.string().optional(),
            base_factor: zod
              .number()
              .min(saveCostRecipeResponseRecipeOneMaterialsItemBaseFactorMin),
            waste_pct: zod
              .number()
              .min(saveCostRecipeResponseRecipeOneMaterialsItemWastePctMin),
            quantity_per_unit: zod
              .number()
              .min(
                saveCostRecipeResponseRecipeOneMaterialsItemQuantityPerUnitMin,
              )
              .optional(),
            unit: zod.string(),
            unit_price: zod
              .number()
              .min(saveCostRecipeResponseRecipeOneMaterialsItemUnitPriceMin),
            pack_size: zod
              .number()
              .gt(
                saveCostRecipeResponseRecipeOneMaterialsItemPackSizeExclusiveMin,
              )
              .optional(),
            round_packs: zod.boolean().optional(),
            assumption: zod.string(),
            dimension_pricing: zod
              .object({
                measure: zod.enum([
                  "length",
                  "area",
                  "volume",
                  "weight",
                  "count",
                ]),
                reference_quantity: zod
                  .number()
                  .gt(
                    saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                  ),
                dimension_labels: zod
                  .array(zod.string())
                  .min(1)
                  .max(
                    saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingDimensionLabelsMax,
                  ),
                fixed_cost: zod
                  .number()
                  .min(
                    saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingFixedCostMin,
                  ),
                reference_price: zod
                  .number()
                  .min(
                    saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferencePriceMin,
                  )
                  .optional(),
                actual_quantity: zod
                  .number()
                  .min(
                    saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingActualQuantityMin,
                  )
                  .optional(),
                resolved_quantity: zod
                  .number()
                  .min(
                    saveCostRecipeResponseRecipeOneMaterialsItemDimensionPricingResolvedQuantityMin,
                  )
                  .optional(),
                resolved_spec: zod.string().optional(),
                section_rates: zod
                  .array(
                    zod.object({
                      section: zod.string(),
                      grade: zod.string(),
                      finish: zod.string(),
                      rate_per_metre: zod.number(),
                      supplier: zod.string(),
                      source_url: zod.string(),
                      checked_on: zod.string(),
                    }),
                  )
                  .optional(),
                length_label: zod.string().optional(),
                selected_section: zod.string().optional(),
                resolved_section: zod.string().optional(),
                evidence: zod.string(),
              })
              .optional(),
          }),
        )
        .max(saveCostRecipeResponseRecipeOneMaterialsMax),
      original_materials: zod
        .array(
          zod.object({
            material_id: zod.string(),
            name: zod.string(),
            spec: zod.string().optional(),
            base_factor: zod
              .number()
              .min(
                saveCostRecipeResponseRecipeOneOriginalMaterialsItemBaseFactorMin,
              ),
            waste_pct: zod
              .number()
              .min(
                saveCostRecipeResponseRecipeOneOriginalMaterialsItemWastePctMin,
              ),
            quantity_per_unit: zod
              .number()
              .min(
                saveCostRecipeResponseRecipeOneOriginalMaterialsItemQuantityPerUnitMin,
              )
              .optional(),
            unit: zod.string(),
            unit_price: zod
              .number()
              .min(
                saveCostRecipeResponseRecipeOneOriginalMaterialsItemUnitPriceMin,
              ),
            pack_size: zod
              .number()
              .gt(
                saveCostRecipeResponseRecipeOneOriginalMaterialsItemPackSizeExclusiveMin,
              )
              .optional(),
            round_packs: zod.boolean().optional(),
            assumption: zod.string(),
            dimension_pricing: zod
              .object({
                measure: zod.enum([
                  "length",
                  "area",
                  "volume",
                  "weight",
                  "count",
                ]),
                reference_quantity: zod
                  .number()
                  .gt(
                    saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                  ),
                dimension_labels: zod
                  .array(zod.string())
                  .min(1)
                  .max(
                    saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingDimensionLabelsMax,
                  ),
                fixed_cost: zod
                  .number()
                  .min(
                    saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingFixedCostMin,
                  ),
                reference_price: zod
                  .number()
                  .min(
                    saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferencePriceMin,
                  )
                  .optional(),
                actual_quantity: zod
                  .number()
                  .min(
                    saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingActualQuantityMin,
                  )
                  .optional(),
                resolved_quantity: zod
                  .number()
                  .min(
                    saveCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingResolvedQuantityMin,
                  )
                  .optional(),
                resolved_spec: zod.string().optional(),
                section_rates: zod
                  .array(
                    zod.object({
                      section: zod.string(),
                      grade: zod.string(),
                      finish: zod.string(),
                      rate_per_metre: zod.number(),
                      supplier: zod.string(),
                      source_url: zod.string(),
                      checked_on: zod.string(),
                    }),
                  )
                  .optional(),
                length_label: zod.string().optional(),
                selected_section: zod.string().optional(),
                resolved_section: zod.string().optional(),
                evidence: zod.string(),
              })
              .optional(),
          }),
        )
        .max(saveCostRecipeResponseRecipeOneOriginalMaterialsMax)
        .optional()
        .describe(
          "Server-owned baseline captured before the first protected admin edit.",
        ),
    }),
    zod.null(),
  ]),
  rateTypical: zod.string(),
  rateLow: zod.string().optional(),
  rateHigh: zod.string().optional(),
  unit: zod.string(),
  recipeSource: zod
    .union([
      zod.literal("default"),
      zod.literal("override"),
      zod.literal("custom"),
      zod.literal(null),
    ])
    .nullable(),
  hasRecipeOverride: zod.boolean(),
  legacyOverride: zod.boolean(),
});

export const ResetCostRecipeParams = zod.object({
  source: zod.enum(["default", "custom"]),
  id: zod.coerce.string(),
});

export const resetCostRecipeResponseRecipeOneLabourAllowanceMin = 0;

export const resetCostRecipeResponseRecipeOnePlantAllowanceMin = 0;

export const resetCostRecipeResponseRecipeOneSupplyAllowanceMin = 0;

export const resetCostRecipeResponseRecipeOneSupplyPricingReferenceQuantityExclusiveMin = 0;

export const resetCostRecipeResponseRecipeOneSupplyPricingDimensionLabelsMax = 3;

export const resetCostRecipeResponseRecipeOneSupplyPricingFixedCostMin = 0;

export const resetCostRecipeResponseRecipeOneSupplyPricingReferencePriceMin = 0;

export const resetCostRecipeResponseRecipeOneSupplyPricingActualQuantityMin = 0;

export const resetCostRecipeResponseRecipeOneSupplyPricingResolvedQuantityMin = 0;

export const resetCostRecipeResponseRecipeOneQuotePricingSelectedRateMin = 0;

export const resetCostRecipeResponseRecipeOneQuotePricingBaselineMaterialsMin = 0;

export const resetCostRecipeResponseRecipeOneQuotePricingBaselineLabourMin = 0;

export const resetCostRecipeResponseRecipeOneQuotePricingBaselinePlantMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemBaseFactorMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemWastePctMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemQuantityPerUnitMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemUnitPriceMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemPackSizeExclusiveMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingFixedCostMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferencePriceMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingActualQuantityMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const resetCostRecipeResponseRecipeOneMaterialsMax = 100;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemBaseFactorMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemWastePctMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemQuantityPerUnitMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemUnitPriceMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemPackSizeExclusiveMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingDimensionLabelsMax = 3;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingFixedCostMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferencePriceMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingActualQuantityMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingResolvedQuantityMin = 0;

export const resetCostRecipeResponseRecipeOneOriginalMaterialsMax = 100;

export const ResetCostRecipeResponse = zod.object({
  hasOriginalMaterials: zod
    .boolean()
    .optional()
    .describe(
      "Protected original materials are available for an administrator to restore. Their rows are not part of the pricing recipe.",
    ),
  recipe: zod.union([
    zod.object({
      version: zod.literal(1),
      shape: zod.enum(["formula", "specification"]),
      labour_allowance: zod
        .number()
        .min(resetCostRecipeResponseRecipeOneLabourAllowanceMin),
      plant_allowance: zod
        .number()
        .min(resetCostRecipeResponseRecipeOnePlantAllowanceMin),
      specification: zod.string().optional(),
      supply_allowance: zod
        .number()
        .min(resetCostRecipeResponseRecipeOneSupplyAllowanceMin)
        .optional(),
      supply_name: zod.string().optional(),
      supply_material_id: zod.string().optional(),
      supply_pricing: zod
        .object({
          measure: zod.enum(["length", "area", "volume", "weight", "count"]),
          reference_quantity: zod
            .number()
            .gt(
              resetCostRecipeResponseRecipeOneSupplyPricingReferenceQuantityExclusiveMin,
            ),
          dimension_labels: zod
            .array(zod.string())
            .min(1)
            .max(
              resetCostRecipeResponseRecipeOneSupplyPricingDimensionLabelsMax,
            ),
          fixed_cost: zod
            .number()
            .min(resetCostRecipeResponseRecipeOneSupplyPricingFixedCostMin),
          reference_price: zod
            .number()
            .min(resetCostRecipeResponseRecipeOneSupplyPricingReferencePriceMin)
            .optional(),
          actual_quantity: zod
            .number()
            .min(resetCostRecipeResponseRecipeOneSupplyPricingActualQuantityMin)
            .optional(),
          resolved_quantity: zod
            .number()
            .min(
              resetCostRecipeResponseRecipeOneSupplyPricingResolvedQuantityMin,
            )
            .optional(),
          resolved_spec: zod.string().optional(),
          section_rates: zod
            .array(
              zod.object({
                section: zod.string(),
                grade: zod.string(),
                finish: zod.string(),
                rate_per_metre: zod.number(),
                supplier: zod.string(),
                source_url: zod.string(),
                checked_on: zod.string(),
              }),
            )
            .optional(),
          length_label: zod.string().optional(),
          selected_section: zod.string().optional(),
          resolved_section: zod.string().optional(),
          evidence: zod.string(),
        })
        .optional(),
      quote_pricing: zod
        .object({
          selected_rate: zod
            .number()
            .min(resetCostRecipeResponseRecipeOneQuotePricingSelectedRateMin),
          baseline_materials: zod
            .number()
            .min(
              resetCostRecipeResponseRecipeOneQuotePricingBaselineMaterialsMin,
            ),
          baseline_labour: zod
            .number()
            .min(resetCostRecipeResponseRecipeOneQuotePricingBaselineLabourMin),
          baseline_plant: zod
            .number()
            .min(resetCostRecipeResponseRecipeOneQuotePricingBaselinePlantMin),
          status: zod.enum(["calculated", "needs_measurements", "manual"]),
          notes: zod.array(zod.string()),
        })
        .optional(),
      status: zod.enum(["none", "reviewed", "user_edited"]),
      materials: zod
        .array(
          zod.object({
            material_id: zod.string(),
            name: zod.string(),
            spec: zod.string().optional(),
            base_factor: zod
              .number()
              .min(resetCostRecipeResponseRecipeOneMaterialsItemBaseFactorMin),
            waste_pct: zod
              .number()
              .min(resetCostRecipeResponseRecipeOneMaterialsItemWastePctMin),
            quantity_per_unit: zod
              .number()
              .min(
                resetCostRecipeResponseRecipeOneMaterialsItemQuantityPerUnitMin,
              )
              .optional(),
            unit: zod.string(),
            unit_price: zod
              .number()
              .min(resetCostRecipeResponseRecipeOneMaterialsItemUnitPriceMin),
            pack_size: zod
              .number()
              .gt(
                resetCostRecipeResponseRecipeOneMaterialsItemPackSizeExclusiveMin,
              )
              .optional(),
            round_packs: zod.boolean().optional(),
            assumption: zod.string(),
            dimension_pricing: zod
              .object({
                measure: zod.enum([
                  "length",
                  "area",
                  "volume",
                  "weight",
                  "count",
                ]),
                reference_quantity: zod
                  .number()
                  .gt(
                    resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                  ),
                dimension_labels: zod
                  .array(zod.string())
                  .min(1)
                  .max(
                    resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingDimensionLabelsMax,
                  ),
                fixed_cost: zod
                  .number()
                  .min(
                    resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingFixedCostMin,
                  ),
                reference_price: zod
                  .number()
                  .min(
                    resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingReferencePriceMin,
                  )
                  .optional(),
                actual_quantity: zod
                  .number()
                  .min(
                    resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingActualQuantityMin,
                  )
                  .optional(),
                resolved_quantity: zod
                  .number()
                  .min(
                    resetCostRecipeResponseRecipeOneMaterialsItemDimensionPricingResolvedQuantityMin,
                  )
                  .optional(),
                resolved_spec: zod.string().optional(),
                section_rates: zod
                  .array(
                    zod.object({
                      section: zod.string(),
                      grade: zod.string(),
                      finish: zod.string(),
                      rate_per_metre: zod.number(),
                      supplier: zod.string(),
                      source_url: zod.string(),
                      checked_on: zod.string(),
                    }),
                  )
                  .optional(),
                length_label: zod.string().optional(),
                selected_section: zod.string().optional(),
                resolved_section: zod.string().optional(),
                evidence: zod.string(),
              })
              .optional(),
          }),
        )
        .max(resetCostRecipeResponseRecipeOneMaterialsMax),
      original_materials: zod
        .array(
          zod.object({
            material_id: zod.string(),
            name: zod.string(),
            spec: zod.string().optional(),
            base_factor: zod
              .number()
              .min(
                resetCostRecipeResponseRecipeOneOriginalMaterialsItemBaseFactorMin,
              ),
            waste_pct: zod
              .number()
              .min(
                resetCostRecipeResponseRecipeOneOriginalMaterialsItemWastePctMin,
              ),
            quantity_per_unit: zod
              .number()
              .min(
                resetCostRecipeResponseRecipeOneOriginalMaterialsItemQuantityPerUnitMin,
              )
              .optional(),
            unit: zod.string(),
            unit_price: zod
              .number()
              .min(
                resetCostRecipeResponseRecipeOneOriginalMaterialsItemUnitPriceMin,
              ),
            pack_size: zod
              .number()
              .gt(
                resetCostRecipeResponseRecipeOneOriginalMaterialsItemPackSizeExclusiveMin,
              )
              .optional(),
            round_packs: zod.boolean().optional(),
            assumption: zod.string(),
            dimension_pricing: zod
              .object({
                measure: zod.enum([
                  "length",
                  "area",
                  "volume",
                  "weight",
                  "count",
                ]),
                reference_quantity: zod
                  .number()
                  .gt(
                    resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferenceQuantityExclusiveMin,
                  ),
                dimension_labels: zod
                  .array(zod.string())
                  .min(1)
                  .max(
                    resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingDimensionLabelsMax,
                  ),
                fixed_cost: zod
                  .number()
                  .min(
                    resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingFixedCostMin,
                  ),
                reference_price: zod
                  .number()
                  .min(
                    resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingReferencePriceMin,
                  )
                  .optional(),
                actual_quantity: zod
                  .number()
                  .min(
                    resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingActualQuantityMin,
                  )
                  .optional(),
                resolved_quantity: zod
                  .number()
                  .min(
                    resetCostRecipeResponseRecipeOneOriginalMaterialsItemDimensionPricingResolvedQuantityMin,
                  )
                  .optional(),
                resolved_spec: zod.string().optional(),
                section_rates: zod
                  .array(
                    zod.object({
                      section: zod.string(),
                      grade: zod.string(),
                      finish: zod.string(),
                      rate_per_metre: zod.number(),
                      supplier: zod.string(),
                      source_url: zod.string(),
                      checked_on: zod.string(),
                    }),
                  )
                  .optional(),
                length_label: zod.string().optional(),
                selected_section: zod.string().optional(),
                resolved_section: zod.string().optional(),
                evidence: zod.string(),
              })
              .optional(),
          }),
        )
        .max(resetCostRecipeResponseRecipeOneOriginalMaterialsMax)
        .optional()
        .describe(
          "Server-owned baseline captured before the first protected admin edit.",
        ),
    }),
    zod.null(),
  ]),
  rateTypical: zod.string(),
  rateLow: zod.string().optional(),
  rateHigh: zod.string().optional(),
  unit: zod.string(),
  recipeSource: zod
    .union([
      zod.literal("default"),
      zod.literal("override"),
      zod.literal("custom"),
      zod.literal(null),
    ])
    .nullable(),
  hasRecipeOverride: zod.boolean(),
  legacyOverride: zod.boolean(),
});

/**
 * Requires a signed-in builder-product administrator. Serves only the indexed server-stored quote-analysis upload, including legacy files without owner ACLs.
 */
export const downloadAdminGeneratedQuoteFilePathFileIndexMin = 0;

export const DownloadAdminGeneratedQuoteFileParams = zod.object({
  id: zod.coerce.string(),
  fileIndex: zod.coerce
    .number()
    .min(downloadAdminGeneratedQuoteFilePathFileIndexMin),
});

export const getPdfTemplateResponseDesignOneCompanyNameSizeMin = 8;
export const getPdfTemplateResponseDesignOneCompanyNameSizeMax = 24;

export const getPdfTemplateResponseDesignOneTextColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const getPdfTemplateResponseDesignOnePrimaryColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const getPdfTemplateResponseDesignOneAccentColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const getPdfTemplateResponseDesignOneCoverTitleMax = 90;

export const getPdfTemplateResponseDesignOneCoverSubtitleMax = 240;

export const getPdfTemplateResponseDesignOneThankYouTitleMax = 90;

export const getPdfTemplateResponseDesignOneThankYouMessageMax = 900;

export const getPdfTemplateResponseDesignOneLogoUrlMax = 500;

export const getPdfTemplateResponseDesignOneCoverArtworkUrlMax = 500;

export const getPdfTemplateResponseDesignOneThankYouArtworkUrlMax = 500;

export const GetPdfTemplateResponse = zod.object({
  design: zod.union([
    zod.object({
      pageVersion: zod
        .union([
          zod.literal(2),
          zod.literal(3),
          zod.literal(4),
          zod.literal(5),
          zod.literal(6),
        ])
        .optional()
        .describe(
          "2 preserves original full-page colours; 3 preserves contrast-adjusted branding; 4 preserves opaque artwork and panels with exact brand colours; 5 preserves translucent framed panels; 6 uses 70% artwork opacity, solid framed panels and no cover total. Omitted in legacy inset designs; never inferred for historical quotes.",
        ),
      layout: zod.enum(["classic", "architect", "bold"]),
      titleFont: zod
        .enum(["helvetica", "poppins", "anton"])
        .optional()
        .describe(
          "Cover and thank-you title typeface. Absence preserves the original Helvetica titles.",
        ),
      companyNameFont: zod
        .enum(["title"])
        .optional()
        .describe(
          "Use the selected title typeface for company-name text alongside the logo. Omitted preserves historical Helvetica company names.",
        ),
      companyNameSize: zod
        .number()
        .min(getPdfTemplateResponseDesignOneCompanyNameSizeMin)
        .max(getPdfTemplateResponseDesignOneCompanyNameSizeMax)
        .optional()
        .describe(
          "Company-name font size in points. New designs use 16; omitted preserves the historical 13-point size.",
        ),
      artworkStyle: zod
        .enum(["modern", "luxury", "minimalist"])
        .optional()
        .describe(
          "Style guidance for newly generated artwork; does not replace existing artwork.",
        ),
      textColor: zod
        .string()
        .regex(getPdfTemplateResponseDesignOneTextColorRegExp)
        .optional()
        .describe(
          "Optional exact colour for branded headings and price text. Omit to retain the original linked main-colour behaviour. Icons and decoration keep their brand colours.",
        ),
      primaryColor: zod
        .string()
        .regex(getPdfTemplateResponseDesignOnePrimaryColorRegExp),
      accentColor: zod
        .string()
        .regex(getPdfTemplateResponseDesignOneAccentColorRegExp),
      coverEnabled: zod.boolean(),
      thankYouEnabled: zod.boolean(),
      useInteriorBranding: zod.boolean(),
      hideLogoBackground: zod
        .boolean()
        .optional()
        .describe(
          "Hide the panel behind the original logo on cover and thank-you pages. Does not edit the logo image. Omitted means the panel remains visible.",
        ),
      removeLogoBackground: zod
        .boolean()
        .optional()
        .describe(
          "Remove a solid edge-connected background from the logo image when rendering. Keeps the logo panel and original uploaded file unchanged. Omitted or false keeps original image pixels.",
        ),
      logoAlignment: zod
        .enum(["left", "center"])
        .optional()
        .describe(
          "Horizontal logo alignment within its identity area. Omitted preserves historical centred placement; new designs use left.",
        ),
      showCompanyName: zod
        .boolean()
        .optional()
        .describe(
          "Whether to display company-name text alongside a logo. Omitted inherits company settings. Without a usable logo, the company name remains visible.",
        ),
      coverTitle: zod
        .string()
        .min(1)
        .max(getPdfTemplateResponseDesignOneCoverTitleMax),
      coverSubtitle: zod
        .string()
        .max(getPdfTemplateResponseDesignOneCoverSubtitleMax),
      thankYouTitle: zod
        .string()
        .min(1)
        .max(getPdfTemplateResponseDesignOneThankYouTitleMax),
      thankYouMessage: zod
        .string()
        .max(getPdfTemplateResponseDesignOneThankYouMessageMax),
      logoUrl: zod
        .string()
        .max(getPdfTemplateResponseDesignOneLogoUrlMax)
        .nullable(),
      coverArtworkUrl: zod
        .string()
        .max(getPdfTemplateResponseDesignOneCoverArtworkUrlMax)
        .nullable(),
      thankYouArtworkUrl: zod
        .string()
        .max(getPdfTemplateResponseDesignOneThankYouArtworkUrlMax)
        .nullable(),
    }),
    zod.null(),
  ]),
  version: zod.string().nullable(),
  profile: zod.object({
    companyName: zod.string(),
    brandColor: zod.string(),
    logoUrl: zod.string().nullable(),
    address: zod.string().optional(),
    phone: zod.string().optional(),
    email: zod.string().optional(),
    website: zod.string().optional(),
    vatNumber: zod.string().optional(),
    showCompanyName: zod.boolean().optional(),
    logoSize: zod.enum(["small", "medium", "large"]).optional(),
    market: zod.string().optional(),
  }),
  ai: zod.object({
    available: zod.boolean(),
    model: zod.string(),
    remaining: zod.number().nullable(),
    limit: zod.number().nullable(),
    unlimited: zod
      .boolean()
      .describe(
        "True only for server-verified builder admins; daily attempt caps do not apply, but concurrency safeguards remain.",
      ),
    nextAvailableAt: zod.coerce
      .date()
      .nullable()
      .describe(
        "When the next personal attempt returns, not a calendar-day reset.",
      ),
    blockedReason: zod
      .union([
        zod.literal("personal_limit"),
        zod.literal("generation_in_progress"),
        zod.literal("service_limit"),
        zod.literal("service_busy"),
        zod.literal(null),
      ])
      .nullable(),
    retryAt: zod.coerce
      .date()
      .nullable()
      .describe(
        "When availability should next be checked. Not a guarantee of capacity.",
      ),
  }),
});

export const savePdfTemplateBodyDesignCompanyNameSizeMin = 8;
export const savePdfTemplateBodyDesignCompanyNameSizeMax = 24;

export const savePdfTemplateBodyDesignTextColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const savePdfTemplateBodyDesignPrimaryColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const savePdfTemplateBodyDesignAccentColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const savePdfTemplateBodyDesignCoverTitleMax = 90;

export const savePdfTemplateBodyDesignCoverSubtitleMax = 240;

export const savePdfTemplateBodyDesignThankYouTitleMax = 90;

export const savePdfTemplateBodyDesignThankYouMessageMax = 900;

export const savePdfTemplateBodyDesignLogoUrlMax = 500;

export const savePdfTemplateBodyDesignCoverArtworkUrlMax = 500;

export const savePdfTemplateBodyDesignThankYouArtworkUrlMax = 500;

export const SavePdfTemplateBody = zod.object({
  design: zod.object({
    pageVersion: zod
      .union([
        zod.literal(2),
        zod.literal(3),
        zod.literal(4),
        zod.literal(5),
        zod.literal(6),
      ])
      .optional()
      .describe(
        "2 preserves original full-page colours; 3 preserves contrast-adjusted branding; 4 preserves opaque artwork and panels with exact brand colours; 5 preserves translucent framed panels; 6 uses 70% artwork opacity, solid framed panels and no cover total. Omitted in legacy inset designs; never inferred for historical quotes.",
      ),
    layout: zod.enum(["classic", "architect", "bold"]),
    titleFont: zod
      .enum(["helvetica", "poppins", "anton"])
      .optional()
      .describe(
        "Cover and thank-you title typeface. Absence preserves the original Helvetica titles.",
      ),
    companyNameFont: zod
      .enum(["title"])
      .optional()
      .describe(
        "Use the selected title typeface for company-name text alongside the logo. Omitted preserves historical Helvetica company names.",
      ),
    companyNameSize: zod
      .number()
      .min(savePdfTemplateBodyDesignCompanyNameSizeMin)
      .max(savePdfTemplateBodyDesignCompanyNameSizeMax)
      .optional()
      .describe(
        "Company-name font size in points. New designs use 16; omitted preserves the historical 13-point size.",
      ),
    artworkStyle: zod
      .enum(["modern", "luxury", "minimalist"])
      .optional()
      .describe(
        "Style guidance for newly generated artwork; does not replace existing artwork.",
      ),
    textColor: zod
      .string()
      .regex(savePdfTemplateBodyDesignTextColorRegExp)
      .optional()
      .describe(
        "Optional exact colour for branded headings and price text. Omit to retain the original linked main-colour behaviour. Icons and decoration keep their brand colours.",
      ),
    primaryColor: zod
      .string()
      .regex(savePdfTemplateBodyDesignPrimaryColorRegExp),
    accentColor: zod.string().regex(savePdfTemplateBodyDesignAccentColorRegExp),
    coverEnabled: zod.boolean(),
    thankYouEnabled: zod.boolean(),
    useInteriorBranding: zod.boolean(),
    hideLogoBackground: zod
      .boolean()
      .optional()
      .describe(
        "Hide the panel behind the original logo on cover and thank-you pages. Does not edit the logo image. Omitted means the panel remains visible.",
      ),
    removeLogoBackground: zod
      .boolean()
      .optional()
      .describe(
        "Remove a solid edge-connected background from the logo image when rendering. Keeps the logo panel and original uploaded file unchanged. Omitted or false keeps original image pixels.",
      ),
    logoAlignment: zod
      .enum(["left", "center"])
      .optional()
      .describe(
        "Horizontal logo alignment within its identity area. Omitted preserves historical centred placement; new designs use left.",
      ),
    showCompanyName: zod
      .boolean()
      .optional()
      .describe(
        "Whether to display company-name text alongside a logo. Omitted inherits company settings. Without a usable logo, the company name remains visible.",
      ),
    coverTitle: zod.string().min(1).max(savePdfTemplateBodyDesignCoverTitleMax),
    coverSubtitle: zod.string().max(savePdfTemplateBodyDesignCoverSubtitleMax),
    thankYouTitle: zod
      .string()
      .min(1)
      .max(savePdfTemplateBodyDesignThankYouTitleMax),
    thankYouMessage: zod
      .string()
      .max(savePdfTemplateBodyDesignThankYouMessageMax),
    logoUrl: zod.string().max(savePdfTemplateBodyDesignLogoUrlMax).nullable(),
    coverArtworkUrl: zod
      .string()
      .max(savePdfTemplateBodyDesignCoverArtworkUrlMax)
      .nullable(),
    thankYouArtworkUrl: zod
      .string()
      .max(savePdfTemplateBodyDesignThankYouArtworkUrlMax)
      .nullable(),
  }),
});

export const savePdfTemplateResponseDesignOneCompanyNameSizeMin = 8;
export const savePdfTemplateResponseDesignOneCompanyNameSizeMax = 24;

export const savePdfTemplateResponseDesignOneTextColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const savePdfTemplateResponseDesignOnePrimaryColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const savePdfTemplateResponseDesignOneAccentColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const savePdfTemplateResponseDesignOneCoverTitleMax = 90;

export const savePdfTemplateResponseDesignOneCoverSubtitleMax = 240;

export const savePdfTemplateResponseDesignOneThankYouTitleMax = 90;

export const savePdfTemplateResponseDesignOneThankYouMessageMax = 900;

export const savePdfTemplateResponseDesignOneLogoUrlMax = 500;

export const savePdfTemplateResponseDesignOneCoverArtworkUrlMax = 500;

export const savePdfTemplateResponseDesignOneThankYouArtworkUrlMax = 500;

export const SavePdfTemplateResponse = zod.object({
  design: zod.union([
    zod.object({
      pageVersion: zod
        .union([
          zod.literal(2),
          zod.literal(3),
          zod.literal(4),
          zod.literal(5),
          zod.literal(6),
        ])
        .optional()
        .describe(
          "2 preserves original full-page colours; 3 preserves contrast-adjusted branding; 4 preserves opaque artwork and panels with exact brand colours; 5 preserves translucent framed panels; 6 uses 70% artwork opacity, solid framed panels and no cover total. Omitted in legacy inset designs; never inferred for historical quotes.",
        ),
      layout: zod.enum(["classic", "architect", "bold"]),
      titleFont: zod
        .enum(["helvetica", "poppins", "anton"])
        .optional()
        .describe(
          "Cover and thank-you title typeface. Absence preserves the original Helvetica titles.",
        ),
      companyNameFont: zod
        .enum(["title"])
        .optional()
        .describe(
          "Use the selected title typeface for company-name text alongside the logo. Omitted preserves historical Helvetica company names.",
        ),
      companyNameSize: zod
        .number()
        .min(savePdfTemplateResponseDesignOneCompanyNameSizeMin)
        .max(savePdfTemplateResponseDesignOneCompanyNameSizeMax)
        .optional()
        .describe(
          "Company-name font size in points. New designs use 16; omitted preserves the historical 13-point size.",
        ),
      artworkStyle: zod
        .enum(["modern", "luxury", "minimalist"])
        .optional()
        .describe(
          "Style guidance for newly generated artwork; does not replace existing artwork.",
        ),
      textColor: zod
        .string()
        .regex(savePdfTemplateResponseDesignOneTextColorRegExp)
        .optional()
        .describe(
          "Optional exact colour for branded headings and price text. Omit to retain the original linked main-colour behaviour. Icons and decoration keep their brand colours.",
        ),
      primaryColor: zod
        .string()
        .regex(savePdfTemplateResponseDesignOnePrimaryColorRegExp),
      accentColor: zod
        .string()
        .regex(savePdfTemplateResponseDesignOneAccentColorRegExp),
      coverEnabled: zod.boolean(),
      thankYouEnabled: zod.boolean(),
      useInteriorBranding: zod.boolean(),
      hideLogoBackground: zod
        .boolean()
        .optional()
        .describe(
          "Hide the panel behind the original logo on cover and thank-you pages. Does not edit the logo image. Omitted means the panel remains visible.",
        ),
      removeLogoBackground: zod
        .boolean()
        .optional()
        .describe(
          "Remove a solid edge-connected background from the logo image when rendering. Keeps the logo panel and original uploaded file unchanged. Omitted or false keeps original image pixels.",
        ),
      logoAlignment: zod
        .enum(["left", "center"])
        .optional()
        .describe(
          "Horizontal logo alignment within its identity area. Omitted preserves historical centred placement; new designs use left.",
        ),
      showCompanyName: zod
        .boolean()
        .optional()
        .describe(
          "Whether to display company-name text alongside a logo. Omitted inherits company settings. Without a usable logo, the company name remains visible.",
        ),
      coverTitle: zod
        .string()
        .min(1)
        .max(savePdfTemplateResponseDesignOneCoverTitleMax),
      coverSubtitle: zod
        .string()
        .max(savePdfTemplateResponseDesignOneCoverSubtitleMax),
      thankYouTitle: zod
        .string()
        .min(1)
        .max(savePdfTemplateResponseDesignOneThankYouTitleMax),
      thankYouMessage: zod
        .string()
        .max(savePdfTemplateResponseDesignOneThankYouMessageMax),
      logoUrl: zod
        .string()
        .max(savePdfTemplateResponseDesignOneLogoUrlMax)
        .nullable(),
      coverArtworkUrl: zod
        .string()
        .max(savePdfTemplateResponseDesignOneCoverArtworkUrlMax)
        .nullable(),
      thankYouArtworkUrl: zod
        .string()
        .max(savePdfTemplateResponseDesignOneThankYouArtworkUrlMax)
        .nullable(),
    }),
    zod.null(),
  ]),
  version: zod.string().nullable(),
  profile: zod.object({
    companyName: zod.string(),
    brandColor: zod.string(),
    logoUrl: zod.string().nullable(),
    address: zod.string().optional(),
    phone: zod.string().optional(),
    email: zod.string().optional(),
    website: zod.string().optional(),
    vatNumber: zod.string().optional(),
    showCompanyName: zod.boolean().optional(),
    logoSize: zod.enum(["small", "medium", "large"]).optional(),
    market: zod.string().optional(),
  }),
  ai: zod.object({
    available: zod.boolean(),
    model: zod.string(),
    remaining: zod.number().nullable(),
    limit: zod.number().nullable(),
    unlimited: zod
      .boolean()
      .describe(
        "True only for server-verified builder admins; daily attempt caps do not apply, but concurrency safeguards remain.",
      ),
    nextAvailableAt: zod.coerce
      .date()
      .nullable()
      .describe(
        "When the next personal attempt returns, not a calendar-day reset.",
      ),
    blockedReason: zod
      .union([
        zod.literal("personal_limit"),
        zod.literal("generation_in_progress"),
        zod.literal("service_limit"),
        zod.literal("service_busy"),
        zod.literal(null),
      ])
      .nullable(),
    retryAt: zod.coerce
      .date()
      .nullable()
      .describe(
        "When availability should next be checked. Not a guarantee of capacity.",
      ),
  }),
});

export const generatePdfTemplateArtworkBodyBriefMax = 600;

export const generatePdfTemplateArtworkBodyLogoUrlMax = 500;

export const generatePdfTemplateArtworkBodyPrimaryColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);
export const generatePdfTemplateArtworkBodyAccentColorRegExp = new RegExp(
  "^#[0-9a-fA-F]{6}$",
);

export const GeneratePdfTemplateArtworkBody = zod.object({
  brief: zod
    .string()
    .max(generatePdfTemplateArtworkBodyBriefMax)
    .optional()
    .describe(
      "Required with at least 10 characters in custom mode. Optional and ignored in automatic mode.",
    ),
  mode: zod
    .enum(["custom", "automatic"])
    .optional()
    .describe(
      "Omitted means custom. Automatic uses the authenticated company's branding without a written brief.",
    ),
  artworkStyle: zod.enum(["modern", "luxury", "minimalist"]).optional(),
  logoUrl: zod
    .string()
    .max(generatePdfTemplateArtworkBodyLogoUrlMax)
    .nullish()
    .describe(
      "An owned template logo or the authenticated user's verified profile logo. Null means no logo reference.",
    ),
  primaryColor: zod
    .string()
    .regex(generatePdfTemplateArtworkBodyPrimaryColorRegExp),
  accentColor: zod
    .string()
    .regex(generatePdfTemplateArtworkBodyAccentColorRegExp),
});

export const GeneratePdfTemplateArtworkResponse = zod.object({
  coverArtworkUrl: zod.string(),
  thankYouArtworkUrl: zod.string(),
});

export const uploadPdfTemplateLogoBodyDataUrlMax = 7000000;

export const UploadPdfTemplateLogoBody = zod.object({
  dataUrl: zod
    .string()
    .max(uploadPdfTemplateLogoBodyDataUrlMax)
    .describe("PNG or JPEG data URL"),
});

export const UploadPdfTemplateLogoResponse = zod.object({
  logoUrl: zod.string(),
});

export const ApplyQuotePdfTemplateParams = zod.object({
  id: zod.coerce.string(),
});

export const ApplyQuotePdfTemplateBody = zod.object({
  useDefault: zod.boolean(),
});

export const ApplyQuotePdfTemplateResponse = zod.object({
  version: zod.string().nullable(),
});

/**
 * @summary Quote examples with illustrative identities, verified quoting methods and measured timings
 */

export const getAutoQuoteExamplesResponseExamplesItemGenerationSecondsMax = 3600;

export const getAutoQuoteExamplesResponseExamplesMax = 30;

export const GetAutoQuoteExamplesResponse = zod.object({
  examples: zod
    .array(
      zod.object({
        id: zod.string(),
        displayName: zod
          .string()
          .describe(
            "Fictional first-name alias, never a real customer identity",
          ),
        companyName: zod
          .string()
          .describe(
            "Fictional company alias, not an endorsement by a real business",
          ),
        quotingMethod: zod
          .enum(["auto", "manual", "template"])
          .describe("Actual method used; never assigned at random"),
        projectType: zod.string(),
        workItemCount: zod.number().min(1),
        source: zod.enum(["customer", "benchmark"]),
        timingScope: zod
          .union([
            zod.literal("pricing"),
            zod.literal("description_and_pricing"),
            zod.literal(null),
          ])
          .nullable()
          .describe(
            "Software processing stages measured; null when no duration was recorded",
          ),
        generationSeconds: zod
          .number()
          .min(1)
          .max(getAutoQuoteExamplesResponseExamplesItemGenerationSecondsMax)
          .nullable()
          .describe(
            "Measured software processing seconds for the specified timingScope, excluding user editing, review and idle time; benchmark times are never presented as customer timings",
          ),
      }),
    )
    .max(getAutoQuoteExamplesResponseExamplesMax),
});

/**
 * @summary Rename or reset one Cost Library navigation entry
 */
export const patchQuoteHeadingNameBodyValueMax = 60;

export const PatchQuoteHeadingNameBody = zod.object({
  key: zod.string(),
  value: zod.string().max(patchQuoteHeadingNameBodyValueMax),
});

export const patchQuoteHeadingNameResponseNamesMaxOne = 60;

export const PatchQuoteHeadingNameResponse = zod.object({
  names: zod.record(
    zod.string(),
    zod.string().max(patchQuoteHeadingNameResponseNamesMaxOne),
  ),
});

export const getQuoteHeadingPreferencesResponseNamesMaxOne = 60;

export const GetQuoteHeadingPreferencesResponse = zod.object({
  names: zod.record(
    zod.string(),
    zod.string().max(getQuoteHeadingPreferencesResponseNamesMaxOne),
  ),
});

export const saveQuoteHeadingPreferencesBodyNamesMaxOne = 60;

export const SaveQuoteHeadingPreferencesBody = zod.object({
  names: zod.record(
    zod.string(),
    zod.string().max(saveQuoteHeadingPreferencesBodyNamesMaxOne),
  ),
});

export const saveQuoteHeadingPreferencesResponseNamesMaxOne = 60;

export const SaveQuoteHeadingPreferencesResponse = zod.object({
  names: zod.record(
    zod.string(),
    zod.string().max(saveQuoteHeadingPreferencesResponseNamesMaxOne),
  ),
});

/**
 * @summary Preview a builder subscription transfer (builder admins only)
 */
export const PreviewSubscriptionTransferParams = zod.object({
  id: zod.coerce.string(),
});

export const previewSubscriptionTransferBodyDestinationEmailMax = 320;

export const PreviewSubscriptionTransferBody = zod.object({
  destinationEmail: zod
    .string()
    .email()
    .max(previewSubscriptionTransferBodyDestinationEmailMax),
});

export const previewSubscriptionTransferResponseCarriedUsageMin = 0;

export const PreviewSubscriptionTransferResponse = zod.object({
  sourceEmail: zod.string(),
  destinationEmail: zod.string(),
  subscriptionId: zod.string(),
  tier: zod.string(),
  periodStart: zod.coerce.date(),
  renewalDate: zod.coerce.date().nullable(),
  cancelAtPeriodEnd: zod.boolean(),
  carriedUsage: zod
    .number()
    .min(previewSubscriptionTransferResponseCarriedUsageMin),
});

/**
 * @summary Move existing subscription ownership without new billing or moving quotes
 */
export const TransferSubscriptionParams = zod.object({
  id: zod.coerce.string(),
});

export const transferSubscriptionBodyDestinationEmailMax = 320;

export const transferSubscriptionBodyExpectedSubscriptionIdMax = 100;

export const TransferSubscriptionBody = zod.object({
  destinationEmail: zod
    .string()
    .email()
    .max(transferSubscriptionBodyDestinationEmailMax),
  expectedSubscriptionId: zod
    .string()
    .min(1)
    .max(transferSubscriptionBodyExpectedSubscriptionIdMax),
  idempotencyKey: zod.string().uuid(),
  ownershipConfirmed: zod.literal(true),
});

export const TransferSubscriptionResponse = zod.object({
  success: zod.boolean(),
  transferId: zod.string(),
  metadataSynced: zod.boolean(),
  message: zod.string(),
});

/**
 * Returns server health status
 * @summary Health check
 */
export const HealthCheckResponse = zod.object({
  status: zod.string(),
});

/**
 * Always returns the same generic response whether the address is unknown, throttled, or eligible.
 * @summary Request an email password reset link
 */
export const requestPasswordResetBodyEmailMax = 320;

export const RequestPasswordResetBody = zod.object({
  email: zod.string().email().max(requestPasswordResetBodyEmailMax),
});

export const RequestPasswordResetResponse = zod.object({
  message: zod.string(),
});

/**
 * @summary Set a new password using a one-time reset token
 */
export const confirmPasswordResetBodyTokenMax = 200;

export const confirmPasswordResetBodyPasswordMin = 8;
export const confirmPasswordResetBodyPasswordMax = 72;

export const ConfirmPasswordResetBody = zod.object({
  token: zod.string().min(1).max(confirmPasswordResetBodyTokenMax),
  password: zod
    .string()
    .min(confirmPasswordResetBodyPasswordMin)
    .max(confirmPasswordResetBodyPasswordMax)
    .describe("At least 8 characters and no more than 72 UTF-8 bytes."),
});

export const ConfirmPasswordResetResponse = zod.object({
  message: zod.string(),
});

/**
 * @summary Send a direct email to a product user
 */
export const SendAdminUserEmailParams = zod.object({
  id: zod.coerce.string(),
});

export const sendAdminUserEmailHeaderIdempotencyKeyMax = 200;

export const SendAdminUserEmailHeader = zod.object({
  "Idempotency-Key": zod
    .string()
    .min(1)
    .max(sendAdminUserEmailHeaderIdempotencyKeyMax)
    .optional()
    .describe(
      "Stable key for this send attempt; alternatively pass idempotencyKey in the JSON body.",
    ),
});

export const sendAdminUserEmailBodySubjectMax = 200;

export const sendAdminUserEmailBodyBodyMax = 20000;

export const sendAdminUserEmailBodyIdempotencyKeyMax = 200;

export const SendAdminUserEmailBody = zod.object({
  subject: zod.string().min(1).max(sendAdminUserEmailBodySubjectMax),
  body: zod.string().min(1).max(sendAdminUserEmailBodyBodyMax),
  product: zod.enum(["builder", "engineer"]),
  replyToMessageId: zod.string().optional(),
  idempotencyKey: zod
    .string()
    .min(1)
    .max(sendAdminUserEmailBodyIdempotencyKeyMax)
    .optional(),
});

export const SendAdminUserEmailResponse = zod.object({
  message: zod.object({
    id: zod.string(),
    userId: zod.string(),
    recipientEmail: zod.string().email(),
    recipientName: zod.string(),
    product: zod.enum(["builder", "engineer"]),
    subject: zod.string(),
    body: zod
      .string()
      .describe("Plain text; incoming HTML-only messages are not rendered."),
    direction: zod.enum(["sent", "received"]),
    createdAt: zod.coerce.date(),
    threadId: zod.string().nullable(),
    fromEmail: zod.string().email().optional(),
  }),
});

/**
 * @summary List sent messages and customer replies for one product
 */
export const getAdminMailQueryProductDefault = `builder`;

export const GetAdminMailQueryParams = zod.object({
  product: zod
    .enum(["builder", "engineer"])
    .default(getAdminMailQueryProductDefault),
});

export const GetAdminMailResponse = zod.object({
  sent: zod.array(
    zod.object({
      id: zod.string(),
      userId: zod.string(),
      recipientEmail: zod.string().email(),
      recipientName: zod.string(),
      product: zod.enum(["builder", "engineer"]),
      subject: zod.string(),
      body: zod
        .string()
        .describe("Plain text; incoming HTML-only messages are not rendered."),
      direction: zod.enum(["sent", "received"]),
      createdAt: zod.coerce.date(),
      threadId: zod.string().nullable(),
      fromEmail: zod.string().email().optional(),
    }),
  ),
  replies: zod.array(
    zod.object({
      id: zod.string(),
      userId: zod.string(),
      recipientEmail: zod.string().email(),
      recipientName: zod.string(),
      product: zod.enum(["builder", "engineer"]),
      subject: zod.string(),
      body: zod
        .string()
        .describe("Plain text; incoming HTML-only messages are not rendered."),
      direction: zod.enum(["sent", "received"]),
      createdAt: zod.coerce.date(),
      threadId: zod.string().nullable(),
      fromEmail: zod.string().email().optional(),
    }),
  ),
  syncError: zod.string().optional(),
});
